import React, { useCallback, useEffect, useRef, useMemo, useState, Component } from 'react';
import { createChart, IChartApi, ISeriesApi, LineSeries } from 'lightweight-charts';

/** 차트 렌더 오류 시 메시지 표시 */
class ChartErrorBoundary extends Component<{ sectionId: string; children: React.ReactNode }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('[chart] render error', this.props.sectionId, error, info.componentStack);
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 8, background: '#f8d7da', color: '#721c24', fontSize: 12 }}>
          차트 렌더 오류: {this.state.error.message}
        </div>
      );
    }
    return this.props.children;
  }
}

/** 0d MultiColumnChart 공통: 데이터 로드 시 로그, hasFlag false 시 사유 표시, 렌더 오류 시 ErrorBoundary로 표시 */
export function MultiChartSectionContent(props: {
  sectionId: string;
  hasFlag: boolean;
  sectionData: Record<string, Orderbook0dEvent[]>;
  columnIds: string[];
  chartHeight: number;
  visibleTimeRange?: VisibleTimeRange;
  /** 툴팁 라벨 (ka10027 매도·매수잔량 등 getKa10027ColumnLabel 전달) */
  columnLabelGetter?: (columnId: string) => string;
}) {
  const { sectionId, hasFlag, sectionData, columnIds, chartHeight, visibleTimeRange, columnLabelGetter } = props;
  // sectionData 참조만 바뀌고 해당 섹션 배열이 같을 때 evs 참조 유지 → 하위 차트 불필요 재렌더/깜빡임 방지
  const evs = useMemo(() => sectionData[sectionId] ?? [], [sectionData[sectionId], sectionId]); // eslint-disable-line react-hooks/exhaustive-deps
  // 1h/ka10095 동적 블록은 columnIds={[colId]}로 매 렌더마다 새 배열 전달 → React.memo 무력화·깜빡임. 내용이 같으면 동일 참조 유지
  const stableColumnIds = useMemo(
    () => columnIds.slice(),
    [columnIds.length, columnIds.length === 1 ? columnIds[0] : columnIds.join(',')] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const hasData = evs.length > 0;
  if (hasData && !hasFlag) {
    return <div style={{ padding: 8, background: '#fff3cd', color: '#856404', fontSize: 12 }}>데이터 {evs.length}건 로드됨. 차트 비표시: 컬럼({columnIds.join(',')}) 미포함.</div>;
  }
  if (hasFlag && hasData) {
    return (
      <ChartErrorBoundary sectionId={sectionId}>
        <MultiColumnChart columnIds={stableColumnIds} events={evs} height={chartHeight} title="" visibleTimeRange={visibleTimeRange} columnLabelGetter={columnLabelGetter} />
      </ChartErrorBoundary>
    );
  }
  return <div style={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#6b7280' }}>데이터가 없습니다</div>;
}

/** 0d 이벤트 한 건: t(ms), data(문서 - 201, 202, 203 등 필드) */
export interface Orderbook0dEvent {
  t: number;
  src?: string;
  data: Record<string, unknown>;
}

/** ka10027(전일대비등락률) 한 행. t는 마이크로초(Unix µs). 차트·테이블·표시 모두 소수 6자리 */
export interface Ka10027Row {
  t: number;
  data: Record<string, unknown>;
}

/** 이벤트 시간 t(마이크로초) → 차트용 초(소수 6자리). 모든 백테스트 이벤트 t는 마이크로초 */
function eventTimeSeconds(t: number): number {
  return t / 1e6;
}

/** t(마이크로초) → "YYYY-MM-DD HH:mm:ss.ffffff" UTC. 표시·테이블·Date 모두 소수 6자리 */
function formatTimeWithMicros(tMicros: number): string {
  const ms = Math.floor(tMicros / 1000);
  const us = Math.floor(tMicros % 1e6);
  return new Date(ms).toISOString().replace('T', ' ').slice(0, 19) + '.' + us.toString().padStart(6, '0');
}

/** 초(소수 포함) → "HH:mm:ss.ffffff" UTC. 차트 축/툴팁용 (차트가 time을 반올림해 넘기므로 실제 데이터 시간으로 포맷할 때 사용) */
function formatSecondsToTimeWithMicros(seconds: number): string {
  const sec = Math.floor(seconds);
  const frac = (seconds - sec).toFixed(6).substring(1);
  const date = new Date(sec * 1000);
  const timeString = date.toLocaleTimeString('en-US', {
    timeZone: 'UTC',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return `${timeString}${frac}`;
}

/** timeLabels 중 chartTime에 가장 가까운 항목의 label 반환. 차트가 time을 반올림해 넘기므로 실제 데이터 기준 6자리 표시용 */
function findClosestTimeLabel(timeLabels: { time: number; label: string }[], chartTime: number): string {
  if (timeLabels.length === 0) return formatSecondsToTimeWithMicros(chartTime);
  const closest = timeLabels.reduce((best, cur) =>
    Math.abs(cur.time - chartTime) < Math.abs(best.time - chartTime) ? cur : best
  );
  return closest.label.slice(11); // "YYYY-MM-DD " 제거 → "HH:mm:ss.ffffff"
}

/** 드롭다운에서 선택된 기간(ms). 테이블 서브기간은 이 범위 안이어야 함 */
export type AllowedTimeRange = { from: number; to: number } | null;

export interface Orderbook0dColumnChartsProps {
  /** 차트별 Redis base 키 (종목 선택 시 설정). 섹션 열 때 baseKey:sectionId 로 조회 */
  baseKey: string | null;
  /** 섹션 ID별 로드된 이벤트 (섹션 열 때 해당 키만 fetch 후 저장) */
  sectionData: Record<string, Orderbook0dEvent[]>;
  /** 백테스트 테이블에서 종목 클릭 시 해당 종목의 매매 수치 (flu_rt 차트 아래 표시) */
  btDetailTrades?: Array<{ buy_rate?: unknown; sell_rate?: unknown; buy_tick_id?: unknown; sell_tick_id?: unknown; profit_rate?: unknown; displayRound?: number; firstCreatedAt?: string }> | null;
  /** 섹션 열릴 때 호출. 해당 섹션 데이터가 없으면 부모가 baseKey:sectionId 로 fetch */
  onNeedSection?: (sectionId: string) => void;
  /** 이번에 로드할 섹션 ID 목록(진행률 표시용). baseKey 변경 시 열린 섹션 목록으로 한 번 호출 */
  onNeedSections?: (sectionIds: string[]) => void;
  /** 차트 높이(px) */
  chartHeight?: number;
  /** 그리드 최대 행 수 (스크롤 가능) */
  maxRows?: number;
  error?: string | null;
  /** 테이블 서브기간이 반드시 들어가야 하는 허용 기간(ms). 드롭다운 선택 기간 */
  allowedTimeRange?: AllowedTimeRange;
}

const NUMERIC_KEY_RE = /^\d+$/;

/** 0d 칼럼 순서 및 라벨 (사용자 지정 순서 그대로) */
export const OD_COLUMN_ORDER: string[] = [
  '21', '41', '61', '81', '51', '71', '91',
  '42', '62', '82', '52', '72', '92',
  '43', '63', '83', '53', '73', '93',
  '44', '64', '84', '54', '74', '94',
  '45', '65', '85', '55', '75', '95',
  '46', '66', '86', '56', '76', '96',
  '47', '67', '87', '57', '77', '97',
  '48', '68', '88', '58', '78', '98',
  '49', '69', '89', '59', '79', '99',
  '50', '70', '60', '90', '80', '100',
  '121', '122', '125', '126', '23', '24', '128', '129', '138', '139',
  '200', '201', '238', '291', '292', '293', '294', '295',
  '6044', '6045', '6046', '6047', '6048', '6049', '6050', '6051', '6052', '6053',
  '6054', '6055', '6056', '6057', '6058', '6059', '6060', '6061', '6062', '6063',
  '6064', '6065', '6066', '6067', '6068', '6069', '6070', '6071', '6072', '6073',
  '6074', '6075', '6076', '6077', '6078', '6079', '6080', '6081', '6082', '6083',
  '6084', '6085', '6086', '6087',
  '6100', '6101', '6102', '6103', '6104', '6105', '6106', '6107', '6108', '6109',
  '6110', '6111', '6112', '6113', '6114', '6115',
];

export const OD_COLUMN_LABELS: Record<string, string> = {
  '21': '호가시간', '41': '매도호가1', '61': '매도호가수량1', '81': '매도호가직전대비1',
  '51': '매수호가1', '71': '매수호가수량1', '91': '매수호가직전대비1',
  '42': '매도호가2', '62': '매도호가수량2', '82': '매도호가직전대비2',
  '52': '매수호가2', '72': '매수호가수량2', '92': '매수호가직전대비2',
  '43': '매도호가3', '63': '매도호가수량3', '83': '매도호가직전대비3',
  '53': '매수호가3', '73': '매수호가수량3', '93': '매수호가직전대비3',
  '44': '매도호가4', '64': '매도호가수량4', '84': '매도호가직전대비4',
  '54': '매수호가4', '74': '매수호가수량4', '94': '매수호가직전대비4',
  '45': '매도호가5', '65': '매도호가수량5', '85': '매도호가직전대비5',
  '55': '매수호가5', '75': '매수호가수량5', '95': '매수호가직전대비5',
  '46': '매도호가6', '66': '매도호가수량6', '86': '매도호가직전대비6',
  '56': '매수호가6', '76': '매수호가수량6', '96': '매수호가직전대비6',
  '47': '매도호가7', '67': '매도호가수량7', '87': '매도호가직전대비7',
  '57': '매수호가7', '77': '매수호가수량7', '97': '매수호가직전대비7',
  '48': '매도호가8', '68': '매도호가수량8', '88': '매도호가직전대비8',
  '58': '매수호가8', '78': '매수호가수량8', '98': '매수호가직전대비8',
  '49': '매도호가9', '69': '매도호가수량9', '89': '매도호가직전대비9',
  '59': '매수호가9', '79': '매수호가수량9', '99': '매수호가직전대비9',
  '50': '매도호가10', '70': '매도호가수량10', '60': '매수호가10', '90': '매도호가직전대비10',
  '80': '매수호가수량10', '100': '매도호가직전대비10',
  '121': '매도호가총잔량', '122': '매도호가총잔량직전대비', '125': '매수호가총잔량', '126': '매수호가총잔량직전대비',
  '23': '예상체결가', '24': '예상체결수량', '128': '순매수잔량', '129': '매수비율', '138': '순매도잔량', '139': '매도비율',
  '200': '예상체결가전일종가대비', '201': '예상체결가전일종가대비등락율', '238': '예상체결가전일종가대비기호',
  '291': '예상체결가', '292': '예상체결량', '293': '예상체결가전일대비기호', '294': '예상체결가전일대비', '295': '예상체결가전일대비등락율',
  '621': 'LP매도호가수량1', '631': 'LP매수호가수량1', '622': 'LP매도호가수량2', '632': 'LP매수호가수량2',
  '623': 'LP매도호가수량3', '633': 'LP매수호가수량3', '624': 'LP매도호가수량4', '634': 'LP매수호가수량4',
  '625': 'LP매도호가수량5', '635': 'LP매수호가수량5', '626': 'LP매도호가수량6', '636': 'LP매수호가수량6',
  '627': 'LP매도호가수량7', '637': 'LP매수호가수량7', '628': 'LP매도호가수량8', '638': 'LP매수호가수량8',
  '629': 'LP매도호가수량9', '639': 'LP매수호가수량9', '630': 'LP매도호가수량10', '640': 'LP매수호가수량10',
  '13': '누적거래량', '299': '전일거래량대비예상체결율', '215': '장운영구분', '216': '투자자별ticker',
  '6044': 'KRX 매도호가잔량1', '6045': 'KRX 매도호가잔량2', '6046': 'KRX 매도호가잔량3', '6047': 'KRX 매도호가잔량4',
  '6048': 'KRX 매도호가잔량5', '6049': 'KRX 매도호가잔량6', '6050': 'KRX 매도호가잔량7', '6051': 'KRX 매도호가잔량8',
  '6052': 'KRX 매도호가잔량9', '6053': 'KRX 매도호가잔량10',
  '6054': 'KRX 매수호가잔량1', '6055': 'KRX 매수호가잔량2', '6056': 'KRX 매수호가잔량3', '6057': 'KRX 매수호가잔량4',
  '6058': 'KRX 매수호가잔량5', '6059': 'KRX 매수호가잔량6', '6060': 'KRX 매수호가잔량7', '6061': 'KRX 매수호가잔량8',
  '6062': 'KRX 매수호가잔량9', '6063': 'KRX 매수호가잔량10', '6064': 'KRX 매도호가총잔량', '6065': 'KRX 매수호가총잔량',
  '6066': 'NXT 매도호가잔량1', '6067': 'NXT 매도호가잔량2', '6068': 'NXT 매도호가잔량3', '6069': 'NXT 매도호가잔량4',
  '6070': 'NXT 매도호가잔량5', '6071': 'NXT 매도호가잔량6', '6072': 'NXT 매도호가잔량7', '6073': 'NXT 매도호가잔량8',
  '6074': 'NXT 매도호가잔량9', '6075': 'NXT 매도호가잔량10',
  '6076': 'NXT 매수호가잔량1', '6077': 'NXT 매수호가잔량2', '6078': 'NXT 매수호가잔량3', '6079': 'NXT 매수호가잔량4',
  '6080': 'NXT 매수호가잔량5', '6081': 'NXT 매수호가잔량6', '6082': 'NXT 매수호가잔량7', '6083': 'NXT 매수호가잔량8',
  '6084': 'NXT 매수호가잔량9', '6085': 'NXT 매수호가잔량10', '6086': 'NXT 매도호가총잔량', '6087': 'NXT 매수호가총잔량',
  '6100': 'KRX 중간가 매도 총잔량 증감', '6101': 'KRX 중간가 매도 총잔량', '6102': 'KRX 중간가', '6103': 'KRX 중간가 매수 총잔량', '6104': 'KRX 중간가 매수 총잔량 증감',
  '6105': 'NXT중간가 매도 총잔량 증감', '6106': 'NXT중간가 매도 총잔량', '6107': 'NXT중간가', '6108': 'NXT중간가 매수 총잔량', '6109': 'NXT중간가 매수 총잔량 증감',
  '6110': 'KRX중간가대비', '6111': 'KRX중간가대비 기호', '6112': 'KRX중간가대비등락율', '6113': 'NXT중간가대비', '6114': 'NXT중간가대비 기호', '6115': 'NXT중간가대비등락율',
};

function toNum(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function formatDelta(prev: number | null, curr: number): string {
  if (prev === null) return '+0.000000';
  const d = curr - prev;
  if (d > 0) return `+${d.toFixed(6)}`;
  if (d < 0) return d.toFixed(6);
  return '+0.000000';
}

/** 컬럼 수집 시 스캔할 최대 행 수 (대량 데이터에서 테이블 연산 부담 완화) */
const COLUMN_SAMPLE_SIZE = 500;

/** 0d 데이터에서 숫자 필드 id 목록 추출. OD_COLUMN_ORDER 순서 적용. 대량 events 시 앞쪽만 샘플링해 스캔 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getNumericColumnIds(events: Orderbook0dEvent[]): string[] {
  const set = new Set<string>();
  const limit = Math.min(events.length, COLUMN_SAMPLE_SIZE);
  for (let i = 0; i < limit; i++) {
    const d = events[i].data && typeof events[i].data === 'object' ? events[i].data : {};
    for (const k of Object.keys(d)) {
      if (NUMERIC_KEY_RE.test(k)) set.add(k);
    }
  }
  const orderIndex = new Map<string, number>();
  OD_COLUMN_ORDER.forEach((id, i) => orderIndex.set(id, i));
  const inOrder: string[] = [];
  const rest: string[] = [];
  Array.from(set).forEach((id) => {
    const idx = orderIndex.get(id);
    if (idx !== undefined) inOrder.push(id);
    else rest.push(id);
  });
  inOrder.sort((a, b) => (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0));
  rest.sort((a, b) => Number(a) - Number(b));
  return [...inOrder, ...rest];
}

/** 차트/기타용: "41 매도호가1" 형태 */
function getColumnLabel(colId: string): string {
  return OD_COLUMN_LABELS[colId] ? `${colId} ${OD_COLUMN_LABELS[colId]}` : colId;
}
/** 테이블 헤더용: 라벨만 "매도호가1" */
function getColumnLabelOnly(colId: string): string {
  return OD_COLUMN_LABELS[colId] ?? colId;
}

/** timeLabels 있으면 실제 데이터 시간(소수 6자리)으로 포맷, 없으면 기본 포맷 (차트가 time을 반올림해 넘기므로) */
function makeTimeScaleOptions(timeLabels?: { time: number; label: string }[]) {
  const tickMarkFormatter = (time: number) =>
    timeLabels && timeLabels.length > 0
      ? findClosestTimeLabel(timeLabels, time)
      : formatSecondsToTimeWithMicros(time);
  return {
    timeVisible: true,
    secondsVisible: true,
    borderColor: '#cccccc',
    rightOffset: 0,
    rightOffsetPixels: 0,
    barSpacing: 0.01,
    lockVisibleTimeRangeOnResize: true,
    rightBarStaysOnScroll: false,
    shiftVisibleRangeOnNewBar: true,
    minBarSpacing: 0.001,
    tickMarkFormatter,
  };
}

const timeScaleOptionsBacktest = makeTimeScaleOptions();

function getSharedChartOptions(
  width: number,
  height: number,
  overrides?: { timeLabels?: { time: number; label: string }[] }
) {
  const timeLabels = overrides?.timeLabels;
  const timeScale = timeLabels ? makeTimeScaleOptions(timeLabels) : timeScaleOptionsBacktest;
  const timeFormatter = (time: number) =>
    timeLabels && timeLabels.length > 0
      ? findClosestTimeLabel(timeLabels, time)
      : formatSecondsToTimeWithMicros(time);
  return {
    width,
    height,
    layout: {
      background: { color: '#ffffff' },
      textColor: '#000000',
      fontSize: 11,
      fontFamily: 'Arial, sans-serif',
    },
    grid: {
      vertLines: { color: '#f8f8f8', style: 1 },
      horzLines: { color: '#f8f8f8', style: 1 },
    },
    rightPriceScale: {
      borderColor: '#cccccc',
      scaleMargins: { top: 0.02, bottom: 0.01 },
      minimumWidth: 100,
    },
    timeScale,
    crosshair: {
      mode: 0,
      vertLine: { color: '#758696', width: 1, style: 3, labelBackgroundColor: '#232832' },
      horzLine: { color: '#758696', width: 1, style: 3, labelBackgroundColor: '#232832' },
    },
    localization: {
      timeFormatter,
    },
    handleScroll: {
      mouseWheel: true,
      pressedMouseMove: true,
      horzTouchDrag: true,
      vertTouchDrag: true,
    },
    handleScale: {
      axisPressedMouseMove: true,
      mouseWheel: true,
      pinch: false,
      axisDoubleClickReset: false,
    },
  } as Parameters<typeof createChart>[1];
}

/** 공통 시간축 정렬용: 초 단위 visible range (모든 차트에 동일 적용) */
export type VisibleTimeRange = { from: number; to: number } | null;

/** lightweight-charts는 time이 엄격한 오름차순이어야 함. 동일 time은 인덱스 기반 소수 오프셋(1e-6초)으로 구분해 누적 오차 없이 처리 */
function ensureStrictlyAscendingTime<T extends { time: number }>(points: T[]): T[] {
  if (points.length === 0) return points;
  const sorted = [...points].sort((a, b) => a.time - b.time);
  let lastTime = -Infinity;
  let runStartIndex = 0;
  let runBaseTime = 0;
  return sorted.map((pt, i) => {
    let t = pt.time;
    if (t <= lastTime) {
      if (i === 0 || sorted[i - 1].time < t) {
        runStartIndex = i;
        runBaseTime = pt.time;
      }
      t = runBaseTime + (i - runStartIndex + 1) * 1e-6;
    } else {
      runStartIndex = i;
      runBaseTime = t;
    }
    lastTime = t;
    return { ...pt, time: t };
  });
}

/** setData 직전 호출: 에러에서 말하는 데이터(index 96721 등)를 콘솔에 상세 출력 */
function logChartDataDetail(label: string, data: { time: number; value?: number }[]): void {
  const L = data.length;
  const problemIndex = 96721;
  if (L > 90000) {
    console.warn(`[chart] ⚠️ setData 큰 데이터 (에러 index=96721 후보): label="${label}", length=${L}`);
  }

  console.group(`[chart] setData 상세: ${label} (length=${L})`);
  console.log('length', L);
  console.log('first 2 items', data.slice(0, 2).map((d, i) => ({ idx: i, ...d, timeType: typeof d.time, timeFixed20: Number(d.time).toFixed(20) })));
  console.log('last 2 items', data.slice(-2).map((d, i) => ({ idx: L - 2 + i, ...d, timeType: typeof d.time, timeFixed20: Number(d.time).toFixed(20) })));

  if (L > problemIndex) {
    const from = Math.max(0, problemIndex - 2);
    const to = Math.min(L, problemIndex + 3);
    const slice = data.slice(from, to);
    console.group('에러 구간 (index 96719~96723 근처)');
    slice.forEach((d, j) => {
      const idx = from + j;
      const prev = idx > 0 ? data[idx - 1] : null;
      console.log(`[${idx}]`, {
        time: d.time,
        value: (d as { value?: number }).value,
        timeType: typeof d.time,
        timeFixed20: Number(d.time).toFixed(20),
        timeNumber: Number(d.time),
        prevTime: prev?.time ?? null,
        prevTimeFixed20: prev != null ? Number(prev.time).toFixed(20) : null,
        strictAsc: prev == null ? true : d.time > prev.time,
        raw: JSON.stringify(d),
      });
    });
    console.groupEnd();
  }

  let firstViolation: number | null = null;
  for (let i = 1; i < data.length; i++) {
    if (data[i].time <= data[i - 1].time) {
      firstViolation = i;
      break;
    }
  }
  if (firstViolation != null) {
    const i = firstViolation;
    const prev = data[i - 1];
    const curr = data[i];
    console.warn('우리 검사 기준 첫 위반 index', i, {
      prev: { time: prev.time, timeFixed20: Number(prev.time).toFixed(20), value: (prev as { value?: number }).value },
      curr: { time: curr.time, timeFixed20: Number(curr.time).toFixed(20), value: (curr as { value?: number }).value },
      slice: data.slice(Math.max(0, i - 2), Math.min(L, i + 3)),
    });
  }
  console.groupEnd();
}

/** time 오름차순 위반 시 콘솔에 문제 구간 출력 */
function logIfTimeNotStrictlyAscending(
  label: string,
  data: { time: number; value?: number }[]
): void {
  logChartDataDetail(label, data);
  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1].time;
    const curr = data[i].time;
    if (curr <= prev) {
      const from = Math.max(0, i - 3);
      const to = Math.min(data.length, i + 3);
      console.warn('[chart] data must be asc ordered by time (위반)', {
        label,
        index: i,
        time: curr,
        prevTime: prev,
        slice: data.slice(from, to).map((d, j) => ({ idx: from + j, time: d.time, timeFixed15: Number(d.time).toFixed(15), value: (d as { value?: number }).value })),
        length: data.length,
      });
      return;
    }
  }
}

/**
 * 축 고정용 더미 데이터. lightweight-charts는 데이터 범위 밖으로 setVisibleRange 불가하므로,
 * 원하는 시간 구간을 축에 쓰려면 구간 양끝(또는 step 간격)에 더미 포인트를 넣어 축을 확장한다.
 * @param range from/to 초
 * @param stepSeconds 없으면 [from, to] 2점만; 있으면 from ~ to 를 step 간격으로 (예: 0.01 = 10ms)
 */
function createAxisAnchorDummyData(
  range: { from: number; to: number },
  stepSeconds?: number
): { time: any; value: number }[] {
  const { from, to } = range;
  if (stepSeconds == null || stepSeconds <= 0) {
    return [
      { time: from, value: 0 },
      { time: to, value: 0 },
    ];
  }
  const out: { time: any; value: number }[] = [];
  let lastTime = -Infinity;
  const nSteps = Math.max(0, Math.ceil((to - from) / stepSeconds));
  for (let i = 0; i <= nSteps; i++) {
    const t = from + i * stepSeconds;
    if (t >= to) {
      if (to > lastTime) out.push({ time: to, value: 0 });
      break;
    }
    if (t > lastTime) {
      out.push({ time: t, value: 0 });
      lastTime = t;
    }
  }
  if (out.length === 0) {
    out.push({ time: from, value: 0 }, { time: to, value: 0 });
  }
  return out;
}

/** 데이터의 시간 범위(초). 가로축 고정용. 1건일 때는 해당 시각 전후 1초 구간 반환 */
function timeRangeFromSeriesData(data: { time: number }[]): { from: number; to: number } | null {
  if (!data.length) return null;
  let min = data[0].time;
  let max = data[0].time;
  for (let i = 1; i < data.length; i++) {
    const t = data[i].time;
    if (t < min) min = t;
    if (t > max) max = t;
  }
  if (min === max) {
    min = min - 1;
    max = max + 1;
  }
  return { from: min, to: max };
}

/** 차트 툴팁용. 표기 순서: 색깔 → 필드명(한국어라벨) → 데이터 */
type ChartTooltipState = {
  visible: boolean;
  x: number;
  y: number;
  time: number | null;
  timeLabel?: string;
  values: { label: string; value: number | null; color: string }[];
};

/** 툴팁 본문: 색깔 · 필드명(한국어라벨) · 데이터 순서 통일. timeLabel 있으면 소수 6자리 표시용 */
function ChartTooltipContent({ time, timeLabel, values }: { time: number | null; timeLabel?: string; values: ChartTooltipState['values'] }) {
  return (
    <>
      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{timeLabel ?? (time != null ? formatTooltipTime(time) : '')}</div>
      {values.map((v, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px', gap: '8px' }}>
          <span style={{ width: 8, height: 8, backgroundColor: v.color, borderRadius: '50%', marginRight: 6, flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', marginRight: '8px' }}>{v.label}</span>
          <span style={{ fontWeight: 'bold', flexShrink: 0 }}>{v.value != null && typeof v.value === 'number' && Number.isFinite(v.value) ? v.value.toLocaleString() : (v.value != null ? String(v.value) : '-')}</span>
        </div>
      ))}
    </>
  );
}

function formatTooltipTime(time: number): string {
  const date = new Date(Math.floor(time) * 1000);
  const timeString = date.toLocaleTimeString('en-US', { timeZone: 'UTC', hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const fractionalSeconds = (time - Math.floor(time)).toFixed(6).substring(1);
  return `${timeString}${fractionalSeconds}`;
}

/** 정렬된 시계열에서 cursorTime에 가장 가까운 점의 value 반환. cursorTime이 데이터 구간 밖(첫 시각 이전/마지막 시각 이후)이면 null → 툴팁 미표시. */
function findClosestValueAtTime(data: { time: number; value: number }[], cursorTime: number): number | null {
  if (!data.length) return null;
  const minT = data[0].time;
  const maxT = data[data.length - 1].time;
  if (cursorTime < minT || cursorTime > maxT) return null;
  let best: { value: number } | null = null;
  let bestDiff = Infinity;
  for (const pt of data) {
    const d = Math.abs(Number(pt.time) - cursorTime);
    if (d < bestDiff) {
      bestDiff = d;
      best = pt;
    }
  }
  return best?.value ?? null;
}

/** 단일 칼럼용 미니 차트 */
const ColumnChart: React.FC<{
  columnId: string;
  events: Orderbook0dEvent[];
  height: number;
  visibleTimeRange?: VisibleTimeRange;
}> = ({ columnId, events, height, visibleTimeRange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const anchorSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const seriesDataRef = useRef<{ time: number; value: number }[]>([]);
  const [tooltip, setTooltip] = useState<ChartTooltipState>({ visible: false, x: 0, y: 0, time: null, values: [] });

  const axisAnchorData = useMemo(
    () => (visibleTimeRange ? createAxisAnchorDummyData(visibleTimeRange, 0.01) : []),
    [visibleTimeRange]
  );

  const seriesData = useMemo(() => {
    const raw = events
      .map((ev) => {
        const v = toNum(ev.data[columnId]);
        if (v === null) return null;
        return { time: eventTimeSeconds(ev.t) as number, value: v };
      })
      .filter((x): x is { time: number; value: number } => x != null);
    return ensureStrictlyAscendingTime(raw).map((pt) => ({ time: pt.time as any, value: pt.value }));
  }, [events, columnId]);

  const timeLabels = useMemo(
    () =>
      events.map((ev) => ({
        time: eventTimeSeconds(ev.t),
        label: formatTimeWithMicros(ev.t),
      })),
    [events]
  );

  const dataRange = useMemo(() => timeRangeFromSeriesData(seriesData), [seriesData]);
  const effectiveRange = visibleTimeRange ?? dataRange;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || seriesData.length === 0 || !effectiveRange) return;
    const containerWidth = Math.max(container.offsetWidth || 400, 400);
    const containerHeight = Math.max(container.clientHeight || height, height);
    const chart = createChart(container, getSharedChartOptions(containerWidth, containerHeight, { timeLabels }));
    const series = chart.addSeries(LineSeries, { color: '#3b82f6', lineWidth: 1 });
    logIfTimeNotStrictlyAscending('ColumnChart', seriesData);
    series.setData(seriesData);
    if (visibleTimeRange && axisAnchorData.length > 0) {
      const anchor = chart.addSeries(LineSeries, {
        color: 'transparent',
        lineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
      });
      anchor.setData(axisAnchorData as any);
      anchorSeriesRef.current = anchor;
    } else {
      anchorSeriesRef.current = null;
    }
    try {
      chart.timeScale().setVisibleRange(effectiveRange as any);
    } catch {
      // ignore
    }
    chartRef.current = chart;
    seriesRef.current = series;
    seriesDataRef.current = seriesData;

    const handleCrosshairMove = (param: { point?: { x: number; y: number }; time?: number }) => {
      if (!param.point || param.time == null || param.point.x < 0 || param.point.y < 0) {
        setTooltip((t) => ({ ...t, visible: false }));
        return;
      }
      const data = seriesDataRef.current;
      const v = findClosestValueAtTime(data, param.time);
      const values: { label: string; value: number; color: string }[] = v != null ? [{ label: getColumnLabel(columnId), value: v, color: '#3b82f6' }] : [];
      let tx = param.point.x + 10;
      let ty = param.point.y - 10;
      if (container) {
        const rect = container.getBoundingClientRect();
        if (tx + 180 > rect.width) tx = param.point.x - 190;
        if (ty < 0) ty = param.point.y + 10;
        if (ty + 60 > rect.height) ty = rect.height - 70;
      }
      const timeLabel = timeLabels.length > 0 ? findClosestTimeLabel(timeLabels, param.time) : undefined;
      setTooltip({ visible: values.length > 0, x: tx, y: ty, time: param.time, timeLabel, values });
    };
    chart.subscribeCrosshairMove(handleCrosshairMove as any);
    const onMouseLeave = () => setTooltip((t) => ({ ...t, visible: false }));
    container.addEventListener('mouseleave', onMouseLeave);
    return () => {
      chart.unsubscribeCrosshairMove(handleCrosshairMove as any);
      container.removeEventListener('mouseleave', onMouseLeave);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      anchorSeriesRef.current = null;
    };
  }, [columnId, height, seriesData, visibleTimeRange, effectiveRange, axisAnchorData, timeLabels]);

  useEffect(() => {
    seriesDataRef.current = seriesData;
  }, [seriesData]);

  useEffect(() => {
    if (!chartRef.current || !seriesRef.current || seriesData.length === 0 || !effectiveRange) return;
    if (anchorSeriesRef.current && axisAnchorData.length > 0) {
      anchorSeriesRef.current.setData(axisAnchorData as any);
    }
    seriesRef.current.setData(seriesData);
    try {
      chartRef.current.timeScale().setVisibleRange(effectiveRange as any);
    } catch {
      // ignore
    }
  }, [seriesData, visibleTimeRange, effectiveRange, axisAnchorData]);

  useEffect(() => {
    if (!chartRef.current || !containerRef.current) return;
    const w = Math.max(containerRef.current.offsetWidth || 400, 400);
    const h = Math.max(containerRef.current.clientHeight || height, height);
    chartRef.current.applyOptions({ width: w, height: h });
  }, [height]);

  const chartAreaStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 320,
    minHeight: height,
    height: `${height}px`,
    position: 'relative',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
  };

  if (seriesData.length === 0) {
    return (
      <div style={chartAreaStyle}>
        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#6b7280' }}>
          데이터가 없습니다
        </div>
      </div>
    );
  }
  return (
    <div style={chartAreaStyle}>
      <div ref={containerRef} style={{ width: '100%', height: '100%', minWidth: 320, minHeight: height, position: 'absolute', left: 0, top: 0 }} />
      {tooltip.visible && tooltip.values.length > 0 && (
        <div style={{ position: 'absolute', left: tooltip.x, top: tooltip.y, backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white', padding: '8px', borderRadius: '4px', fontSize: '12px', zIndex: 1000, pointerEvents: 'none', maxWidth: '200px' }}>
          <ChartTooltipContent time={tooltip.time} timeLabel={tooltip.timeLabel} values={tooltip.values} />
        </div>
      )}
    </div>
  );
};

/** ka10027 flu_rt(등락률) 전용 라인 차트 */
const Ka10027FluRtChart: React.FC<{ ka10027Events: Ka10027Row[]; height: number; visibleTimeRange?: VisibleTimeRange }> = ({ ka10027Events, height, visibleTimeRange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const anchorSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const seriesDataRef = useRef<{ time: number; value: number }[]>([]);
  const [tooltip, setTooltip] = useState<ChartTooltipState>({ visible: false, x: 0, y: 0, time: null, values: [] });

  const axisAnchorData = useMemo(
    () => (visibleTimeRange ? createAxisAnchorDummyData(visibleTimeRange, 0.01) : []),
    [visibleTimeRange]
  );

  const seriesData = useMemo(() => {
    const raw = ka10027Events
      .map((ev) => {
        const v = toNum(ev.data?.flu_rt);
        if (v === null) return null;
        return { time: eventTimeSeconds(ev.t) as number, value: v };
      })
      .filter((x): x is { time: number; value: number } => x != null);
    return ensureStrictlyAscendingTime(raw).map((pt) => ({ time: pt.time as any, value: pt.value }));
  }, [ka10027Events]);

  const timeLabels = useMemo(
    () =>
      ka10027Events.map((ev) => ({
        time: eventTimeSeconds(ev.t),
        label: formatTimeWithMicros(ev.t),
      })),
    [ka10027Events]
  );

  const dataRange = useMemo(() => timeRangeFromSeriesData(seriesData), [seriesData]);
  const effectiveRange = visibleTimeRange ?? dataRange;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || seriesData.length === 0 || !effectiveRange) return;
    const containerWidth = Math.max(container.offsetWidth || 400, 400);
    const containerHeight = Math.max(container.clientHeight || height, height);
    const chart = createChart(container, getSharedChartOptions(containerWidth, containerHeight, { timeLabels }));
    const series = chart.addSeries(LineSeries, { color: '#3b82f6', lineWidth: 1 });
    logIfTimeNotStrictlyAscending('Ka10027FluRtChart', seriesData);
    series.setData(seriesData);
    if (visibleTimeRange && axisAnchorData.length > 0) {
      const anchor = chart.addSeries(LineSeries, {
        color: 'transparent',
        lineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
      });
      anchor.setData(axisAnchorData as any);
      anchorSeriesRef.current = anchor;
    } else {
      anchorSeriesRef.current = null;
    }
    try {
      chart.timeScale().setVisibleRange(effectiveRange as any);
    } catch {
      // ignore
    }
    chartRef.current = chart;
    seriesRef.current = series;
    seriesDataRef.current = seriesData;

    const handleCrosshairMove = (param: { point?: { x: number; y: number }; time?: number }) => {
      if (!param.point || param.time == null || param.point.x < 0 || param.point.y < 0) {
        setTooltip((t) => ({ ...t, visible: false }));
        return;
      }
      const v = findClosestValueAtTime(seriesDataRef.current, param.time);
      const values: { label: string; value: number; color: string }[] = v != null ? [{ label: getKa10027ColumnLabel('flu_rt'), value: v, color: '#3b82f6' }] : [];
      let tx = param.point.x + 10;
      let ty = param.point.y - 10;
      if (container) {
        const rect = container.getBoundingClientRect();
        if (tx + 180 > rect.width) tx = param.point.x - 190;
        if (ty < 0) ty = param.point.y + 10;
        if (ty + 60 > rect.height) ty = rect.height - 70;
      }
      const timeLabel = timeLabels.length > 0 ? findClosestTimeLabel(timeLabels, param.time) : undefined;
      setTooltip({ visible: values.length > 0, x: tx, y: ty, time: param.time, timeLabel, values });
    };
    chart.subscribeCrosshairMove(handleCrosshairMove as any);
    const onMouseLeave = () => setTooltip((t) => ({ ...t, visible: false }));
    container.addEventListener('mouseleave', onMouseLeave);
    return () => {
      chart.unsubscribeCrosshairMove(handleCrosshairMove as any);
      container.removeEventListener('mouseleave', onMouseLeave);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      anchorSeriesRef.current = null;
    };
  }, [height, seriesData, visibleTimeRange, effectiveRange, axisAnchorData, timeLabels]);

  useEffect(() => {
    seriesDataRef.current = seriesData;
  }, [seriesData]);

  useEffect(() => {
    if (!chartRef.current || !seriesRef.current || seriesData.length === 0 || !effectiveRange) return;
    if (anchorSeriesRef.current && axisAnchorData.length > 0) {
      anchorSeriesRef.current.setData(axisAnchorData as any);
    }
    seriesRef.current.setData(seriesData);
    try {
      chartRef.current.timeScale().setVisibleRange(effectiveRange as any);
    } catch {
      // ignore
    }
  }, [seriesData, visibleTimeRange, effectiveRange, axisAnchorData]);

  useEffect(() => {
    if (!chartRef.current || !containerRef.current) return;
    const w = Math.max(containerRef.current.offsetWidth || 400, 400);
    const h = Math.max(containerRef.current.clientHeight || height, height);
    chartRef.current.applyOptions({ width: w, height: h });
  }, [height]);

  const chartAreaStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 320,
    minHeight: height,
    height: `${height}px`,
    position: 'relative',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
  };

  if (seriesData.length === 0) {
    return (
      <div style={chartAreaStyle}>
        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#6b7280' }}>
          데이터가 없습니다
        </div>
      </div>
    );
  }
  return (
    <div style={chartAreaStyle}>
      <div ref={containerRef} style={{ width: '100%', height: '100%', minWidth: 320, minHeight: height, position: 'absolute', left: 0, top: 0 }} />
      {tooltip.visible && tooltip.values.length > 0 && (
        <div style={{ position: 'absolute', left: tooltip.x, top: tooltip.y, backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white', padding: '8px', borderRadius: '4px', fontSize: '12px', zIndex: 1000, pointerEvents: 'none', maxWidth: '200px' }}>
          <ChartTooltipContent time={tooltip.time} timeLabel={tooltip.timeLabel} values={tooltip.values} />
        </div>
      )}
    </div>
  );
};

/** [ka10027] 단일 필드 시계열 차트 (flu_rt와 동일 패턴, fieldKey만 다름) */
const Ka10027FieldChart: React.FC<{ ka10027Events: Ka10027Row[]; fieldKey: string; title: string; height: number; visibleTimeRange?: VisibleTimeRange }> = ({ ka10027Events, fieldKey, title, height, visibleTimeRange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const anchorSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const seriesDataRef = useRef<{ time: number; value: number }[]>([]);
  const [tooltip, setTooltip] = useState<ChartTooltipState>({ visible: false, x: 0, y: 0, time: null, values: [] });

  const axisAnchorData = useMemo(
    () => (visibleTimeRange ? createAxisAnchorDummyData(visibleTimeRange, 0.01) : []),
    [visibleTimeRange]
  );

  const seriesData = useMemo(() => {
    const raw = ka10027Events
      .map((ev) => {
        const v = toNum(ev.data?.[fieldKey as keyof typeof ev.data]);
        if (v === null) return null;
        return { time: eventTimeSeconds(ev.t) as number, value: v };
      })
      .filter((x): x is { time: number; value: number } => x != null);
    return ensureStrictlyAscendingTime(raw).map((pt) => ({ time: pt.time as any, value: pt.value }));
  }, [ka10027Events, fieldKey]);

  const timeLabels = useMemo(
    () =>
      ka10027Events.map((ev) => ({
        time: eventTimeSeconds(ev.t),
        label: formatTimeWithMicros(ev.t),
      })),
    [ka10027Events]
  );

  const dataRange = useMemo(() => timeRangeFromSeriesData(seriesData), [seriesData]);
  const effectiveRange = visibleTimeRange ?? dataRange;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || seriesData.length === 0 || !effectiveRange) return;
    const containerWidth = Math.max(container.offsetWidth || 400, 400);
    const containerHeight = Math.max(container.clientHeight || height, height);
    const chart = createChart(container, getSharedChartOptions(containerWidth, containerHeight, { timeLabels }));
    const series = chart.addSeries(LineSeries, { color: '#3b82f6', lineWidth: 1 });
    logIfTimeNotStrictlyAscending('Ka10027FieldChart', seriesData);
    series.setData(seriesData);
    if (visibleTimeRange && axisAnchorData.length > 0) {
      const anchor = chart.addSeries(LineSeries, {
        color: 'transparent',
        lineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
      });
      anchor.setData(axisAnchorData as any);
      anchorSeriesRef.current = anchor;
    } else {
      anchorSeriesRef.current = null;
    }
    try {
      chart.timeScale().setVisibleRange(effectiveRange as any);
    } catch {
      // ignore
    }
    chartRef.current = chart;
    seriesRef.current = series;
    seriesDataRef.current = seriesData;

    const handleCrosshairMove = (param: { point?: { x: number; y: number }; time?: number }) => {
      if (!param.point || param.time == null || param.point.x < 0 || param.point.y < 0) {
        setTooltip((t) => ({ ...t, visible: false }));
        return;
      }
      const v = findClosestValueAtTime(seriesDataRef.current, param.time);
      const values: { label: string; value: number; color: string }[] = v != null ? [{ label: getKa10027ColumnLabel(fieldKey), value: v, color: '#3b82f6' }] : [];
      let tx = param.point.x + 10;
      let ty = param.point.y - 10;
      if (container) {
        const rect = container.getBoundingClientRect();
        if (tx + 180 > rect.width) tx = param.point.x - 190;
        if (ty < 0) ty = param.point.y + 10;
        if (ty + 60 > rect.height) ty = rect.height - 70;
      }
      const timeLabel = timeLabels.length > 0 ? findClosestTimeLabel(timeLabels, param.time) : undefined;
      setTooltip({ visible: values.length > 0, x: tx, y: ty, time: param.time, timeLabel, values });
    };
    chart.subscribeCrosshairMove(handleCrosshairMove as any);
    const onMouseLeave = () => setTooltip((t) => ({ ...t, visible: false }));
    container.addEventListener('mouseleave', onMouseLeave);
    return () => {
      chart.unsubscribeCrosshairMove(handleCrosshairMove as any);
      container.removeEventListener('mouseleave', onMouseLeave);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      anchorSeriesRef.current = null;
    };
  }, [height, seriesData, visibleTimeRange, effectiveRange, axisAnchorData, fieldKey, timeLabels]);

  useEffect(() => {
    seriesDataRef.current = seriesData;
  }, [seriesData]);

  useEffect(() => {
    if (!chartRef.current || !seriesRef.current || seriesData.length === 0 || !effectiveRange) return;
    if (anchorSeriesRef.current && axisAnchorData.length > 0) {
      anchorSeriesRef.current.setData(axisAnchorData as any);
    }
    seriesRef.current.setData(seriesData);
    try {
      chartRef.current.timeScale().setVisibleRange(effectiveRange as any);
    } catch {
      // ignore
    }
  }, [seriesData, visibleTimeRange, effectiveRange, axisAnchorData]);

  useEffect(() => {
    if (!chartRef.current || !containerRef.current) return;
    const w = Math.max(containerRef.current.offsetWidth || 400, 400);
    const h = Math.max(containerRef.current.clientHeight || height, height);
    chartRef.current.applyOptions({ width: w, height: h });
  }, [height]);

  const chartAreaStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 320,
    minHeight: height,
    height: `${height}px`,
    position: 'relative',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
  };

  if (seriesData.length === 0) {
    return (
      <div style={chartAreaStyle}>
        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#6b7280' }}>
          데이터가 없습니다
        </div>
      </div>
    );
  }
  return (
    <div style={chartAreaStyle}>
      <div ref={containerRef} style={{ width: '100%', height: '100%', minWidth: 320, minHeight: height, position: 'absolute', left: 0, top: 0 }} />
      {tooltip.visible && tooltip.values.length > 0 && (
        <div style={{ position: 'absolute', left: tooltip.x, top: tooltip.y, backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white', padding: '8px', borderRadius: '4px', fontSize: '12px', zIndex: 1000, pointerEvents: 'none', maxWidth: '200px' }}>
          <ChartTooltipContent time={tooltip.time} timeLabel={tooltip.timeLabel} values={tooltip.values} />
        </div>
      )}
    </div>
  );
};

/** 테이블 행 높이 통일 (데이터 행·빈 행 동일) */
const TABLE_ROW_HEIGHT = 40;
/** 테이블 영역 기본 높이 (데이터 유무와 관계없이 고정) */
const TABLE_CONTAINER_HEIGHT = 360 * 2;

/** 테이블 표시 (sectionData 기반, Redis 섹션별 키에서 로드) */
const SHOW_TABLES = true;

/** 테이블에 계산·표시할 최대 행 수 (서브기간 미적용 시 참고용, 현재는 서브기간 적용 시만 테이블 표시) */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MAX_TABLE_ROWS_TO_COMPUTE = 10000;
/** 서브기간 적용 시 기간 내 전체 표시용 상한 */
const MAX_TABLE_ROWS_WHEN_FILTERED = 100000;
/** 점진 표시: 처음 보여줄 행 수 */
const INITIAL_TABLE_VISIBLE = 100;
/** 점진 표시: 한 번에 추가할 행 수 */
const TABLE_BATCH_SIZE = 200;

/** 매도호가1~10 (41~50) 한 차트에 여러 시리즈 */
const MAEDO_HOGA_COLUMN_IDS = ['41', '42', '43', '44', '45', '46', '47', '48', '49', '50'];
/** 매도호가수량1~10 (61~70) 한 차트에 여러 시리즈 */
const MAEDO_HOGA_SUYANG_COLUMN_IDS = ['61', '62', '63', '64', '65', '66', '67', '68', '69', '70'];
/** 매도호가직전대비1~10 (81~90) 한 차트에 여러 시리즈 */
const MAEDO_HOGA_JIKJEON_COLUMN_IDS = ['81', '82', '83', '84', '85', '86', '87', '88', '89', '90'];

/** 매수호가1~10 (51~60) 한 차트에 여러 시리즈 */
const MASU_HOGA_COLUMN_IDS = ['51', '52', '53', '54', '55', '56', '57', '58', '59', '60'];
/** 매수호가수량1~10 (71~80) 한 차트에 여러 시리즈 */
const MASU_HOGA_SUYANG_COLUMN_IDS = ['71', '72', '73', '74', '75', '76', '77', '78', '79', '80'];
/** 매수호가직전대비1~10 (91~100) 한 차트에 여러 시리즈 */
const MASU_HOGA_JIKJEON_COLUMN_IDS = ['91', '92', '93', '94', '95', '96', '97', '98', '99', '100'];

/** KRX 매도호가잔량1~10 (6044~6053) 한 차트에 여러 시리즈 */
const KRX_MAEDO_JAERYANG_COLUMN_IDS = ['6044', '6045', '6046', '6047', '6048', '6049', '6050', '6051', '6052', '6053'];
/** KRX 매수호가잔량1~10 (6054~6063) 한 차트에 여러 시리즈 */
const KRX_MASU_JAERYANG_COLUMN_IDS = ['6054', '6055', '6056', '6057', '6058', '6059', '6060', '6061', '6062', '6063'];
/** NXT 매도호가잔량1~10 (6066~6075) 한 차트에 여러 시리즈 */
const NXT_MAEDO_JAERYANG_COLUMN_IDS = ['6066', '6067', '6068', '6069', '6070', '6071', '6072', '6073', '6074', '6075'];
/** NXT 매수호가잔량1~10 (6076~6085) 한 차트에 여러 시리즈 */
const NXT_MASU_JAERYANG_COLUMN_IDS = ['6076', '6077', '6078', '6079', '6080', '6081', '6082', '6083', '6084', '6085'];

/** 122 매도호가총잔량직전대비, 126 매수호가총잔량직전대비 한 차트에 통합 */
const HOGA_JAERYANG_JIKJEON_COLUMN_IDS = ['122', '126'];
/** 128 순매수잔량, 138 순매도잔량 한 차트에 통합 */
const SUNMAESU_JAERYANG_COLUMN_IDS = ['128', '138'];
/** 129 매수비율, 139 매도비율 한 차트에 통합 */
const BIRYU_COLUMN_IDS = ['129', '139'];
/** 121 매도호가총잔량, 125 매수호가총잔량 한 차트에 통합 */
const MAEDO_MASU_HOGA_JAERYANG_COLUMN_IDS = ['121', '125'];
/** 6064 KRX 매도호가총잔량, 6065 KRX 매수호가총잔량 한 차트에 통합 */
const KRX_JAERYANG_COLUMN_IDS = ['6064', '6065'];
/** 6100 KRX 중간가 매도 총잔량 증감, 6104 KRX 중간가 매수 총잔량 증감 한 차트에 통합 */
const KRX_JAERYANG_JEUNGGAM_COLUMN_IDS = ['6100', '6104'];
/** 6101 KRX 중간가 매도 총잔량, 6103 KRX 중간가 매수 총잔량 한 차트에 통합 */
const KRX_JAERYANG_TOTAL_COLUMN_IDS = ['6101', '6103'];
/** 6086 NXT 매도호가총잔량, 6087 NXT 매수호가총잔량 한 차트에 통합 */
const NXT_JAERYANG_COLUMN_IDS = ['6086', '6087'];
/** NXT중간가 매도 총잔량 증감(6105), NXT중간가 매수 총잔량 증감(6109) 한 차트에 통합 */
const NXT_JAERYANG_JEUNGGAM_COLUMN_IDS = ['6105', '6109'];
/** NXT중간가 매도 총잔량(6106), NXT중간가 매수 총잔량(6108) 한 차트에 통합 */
const NXT_JAERYANG_JEUNGGAM_TOTAL_COLUMN_IDS = ['6106', '6108'];
/** 6111 KRX중간가대비 기호, 6114 NXT중간가대비 기호 한 차트에 통합 */
const KRX_NXT_JOONGGA_SIG_COLUMN_IDS = ['6111', '6114'];
/** 6110 KRX중간가대비, 6113 NXT중간가대비 한 차트에 통합 */
const KRX_NXT_JOONGGA_DAEBI_COLUMN_IDS = ['6110', '6113'];
/** 6102 KRX 중간가, 6107 NXT중간가 한 차트에 통합 */
const KRX_NXT_JOONGGA_COLUMN_IDS = ['6102', '6107'];

/** 24 예상체결수량, 292 예상체결량 한 차트에 통합 */
export const EXPECTED_CNTR_COLUMN_IDS = ['24', '292'];
/** 201 예상체결가전일종가대비등락율, 295 예상체결가전일대비등락율 한 차트에 통합 */
const EXPECTED_CNTR_FLU_RT_COLUMN_IDS = ['201', '295'];
/** 238 예상체결가전일종가대비기호, 293 예상체결가전일대비기호 한 차트에 통합 */
const EXPECTED_CNTR_SIG_COLUMN_IDS = ['238', '293'];
/** 6112 KRX중간가대비등락율, 6115 NXT중간가대비등락율 한 차트에 통합 */
const KRX_NXT_JOONGGA_FLU_RT_COLUMN_IDS = ['6112', '6115'];
/** 294 예상체결가전일대비, 200 예상체결가전일종가대비 한 차트에 통합 */
const EXPECTED_CNTR_PRE_COLUMN_IDS = ['294', '200'];
/** 23 예상체결가, 291 예상체결가 한 차트에 통합 */
const EXPECTED_CNTR_PRIC_COLUMN_IDS = ['23', '291'];
/** 41/51 차트 아래 고정 배치할 단일 차트 (비움: 291은 23과 통합) */
const FIXED_CHART_COLUMN_IDS: string[] = [];

/** 차트 섹션 기본 순서 (고정 ID만; 0d-single-*, 1h-*, ka10095-* 는 런타임에 병합) */
const CHART_SECTION_ORDER_FIXED: string[] = [
  'ka10027-flu_rt', 'ka10027-jaeryang', 'ka10027-cntr_str', 'ka10027-cur_prc', 'ka10027-now_trde_qty', 'ka10027-pred_pre',
  '0d-maedo-hoga', '0d-masu-hoga',
  '0d-expected-cntr-pric', '0d-expected-cntr', '0d-expected-cntr-flurt', '0d-expected-cntr-sig', '0d-expected-cntr-pre',
  '0d-maedo-masu-jaeryang', '0d-maedo-hoga-suyang', '0d-maedo-hoga-jikjeon', '0d-masu-hoga-suyang', '0d-masu-hoga-jikjeon',
  '0d-krx-maedo-jaeryang', '0d-krx-masu-jaeryang', '0d-nxt-maedo-jaeryang', '0d-nxt-masu-jaeryang',
  '0d-hoga-jaeryang-jikjeon', '0d-biryu', '0d-sunmaesu-jaeryang',
  '0d-krx-jaeryang', '0d-krx-jaeryang-jeunggam', '0d-krx-jaeryang-total', '0d-nxt-jaeryang', '0d-nxt-jaeryang-jeunggam', '0d-nxt-jaeryang-total',
  '0d-krx-nxt-joongga-sig', '0d-krx-nxt-joongga-daebi', '0d-krx-nxt-joongga', '0d-krx-nxt-joongga-flurt',
  '0c-27-28',
  'ka10095-sel-bids', 'ka10095-buy-bids', 'ka10095-ohlc', 'ka10095-upl-lst', 'ka10095-bids', 'ka10095-pri-jaeryang', 'ka10095-tot-jaeryang',
];

const MULTI_CHART_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#eab308', '#a855f7', '#ec4899', '#06b6d4', '#f97316', '#84cc16', '#6366f1'];

/** 0d 이벤트에서 컬럼 값 추출 (data 직하위 또는 data.values 하위 지원) */
function getEventDataValue(ev: Orderbook0dEvent, columnId: string): unknown {
  const d = ev.data;
  if (!d || typeof d !== 'object') return undefined;
  const direct = (d as Record<string, unknown>)[columnId];
  if (direct !== undefined) return direct;
  const values = (d as Record<string, unknown>).values;
  if (values && typeof values === 'object' && !Array.isArray(values)) return (values as Record<string, unknown>)[columnId];
  return undefined;
}

const MultiColumnChartInner: React.FC<{
  columnIds: string[];
  events: Orderbook0dEvent[];
  height: number;
  title: string;
  visibleTimeRange?: VisibleTimeRange;
  /** 툴팁 라벨 (미지정 시 getColumnLabel 사용, ka10027 등은 getKa10027ColumnLabel 전달) */
  columnLabelGetter?: (columnId: string) => string;
}> = ({ columnIds, events, height, visibleTimeRange, columnLabelGetter }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRefs = useRef<(ISeriesApi<'Line'> | null)[]>([]);
  const anchorSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const crosshairHandlerRef = useRef<((param: any) => void) | null>(null);
  const mouseLeaveRef = useRef<(() => void) | null>(null);
  const allSeriesDataRef = useRef<{ time: number; value: number }[][]>([]);
  const columnIdsRef = useRef<string[]>([]);
  const lastSizeRef = useRef<{ w: number; h: number } | null>(null);
  const [tooltip, setTooltip] = useState<ChartTooltipState>({ visible: false, x: 0, y: 0, time: null, values: [] });

  /** 고정 시간축용: visibleTimeRange가 있으면 축 확장을 위한 더미 데이터(0.01초 간격) */
  const axisAnchorData = useMemo(
    () => (visibleTimeRange ? createAxisAnchorDummyData(visibleTimeRange, 0.01) : []),
    [visibleTimeRange]
  );

  const allSeriesData = useMemo(() => {
    return columnIds.map((columnId) => {
      const raw = events
        .map((ev) => {
          const v = toNum(getEventDataValue(ev, columnId));
          if (v === null) return null;
          return { time: eventTimeSeconds(ev.t) as number, value: v };
        })
        .filter((x): x is { time: number; value: number } => x != null);
      return ensureStrictlyAscendingTime(raw).map((pt) => ({ time: pt.time as any, value: pt.value }));
    });
  }, [events, columnIds]);

  const timeLabels = useMemo(
    () =>
      events.map((ev) => ({
        time: eventTimeSeconds(ev.t),
        label: formatTimeWithMicros(ev.t),
      })),
    [events]
  );

  const dataRange = useMemo(
    () => timeRangeFromSeriesData(allSeriesData.flatMap((d) => d)),
    [allSeriesData],
  );
  const effectiveRange = visibleTimeRange ?? dataRange;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!effectiveRange) return;
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (cancelled || !container) return;
      const containerWidth = Math.max(container.offsetWidth || 400, 400);
      const containerHeight = Math.max(container.clientHeight || height, height);
      const chart = createChart(container, getSharedChartOptions(containerWidth, containerHeight, { timeLabels }));
      const refs: (ISeriesApi<'Line'> | null)[] = [];
      columnIds.forEach((colId, idx) => {
        const color = MULTI_CHART_COLORS[idx % MULTI_CHART_COLORS.length];
        const series = chart.addSeries(LineSeries, { color, lineWidth: 1 });
        const data = allSeriesData[idx];
        if (data.length > 0) {
          logIfTimeNotStrictlyAscending(`MultiColumnChart series ${idx} (${colId})`, data);
          series.setData(data);
        }
        refs.push(series);
      });
      if (visibleTimeRange && axisAnchorData.length > 0) {
        const anchor = chart.addSeries(LineSeries, {
          color: 'transparent',
          lineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          priceLineVisible: false,
        });
        anchor.setData(axisAnchorData as any);
        anchorSeriesRef.current = anchor;
      } else {
        anchorSeriesRef.current = null;
      }
      seriesRefs.current = refs;
      chartRef.current = chart;
      allSeriesDataRef.current = allSeriesData;
      columnIdsRef.current = columnIds;

      const handleCrosshairMove = (param: { point?: { x: number; y: number }; time?: number }) => {
        if (!param.point || param.time == null || param.point.x < 0 || param.point.y < 0) {
          setTooltip((t) => ({ ...t, visible: false }));
          return;
        }
        const dataList = allSeriesDataRef.current;
        const ids = columnIdsRef.current;
        const values: { label: string; value: number | null; color: string }[] = [];
        ids.forEach((colId, idx) => {
          const label = colId ? (columnLabelGetter ?? getColumnLabel)(colId) : '';
          const v = dataList[idx] ? findClosestValueAtTime(dataList[idx], param.time!) : null;
          values.push({ label, value: v ?? null, color: MULTI_CHART_COLORS[idx % MULTI_CHART_COLORS.length] });
        });
        const hasAny = values.some((r) => r.value != null);
        let tx = param.point.x + 10;
        let ty = param.point.y - 10;
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const tooltipHeight = Math.min(300, values.length * 20 + 40);
          if (tx + 200 > rect.width) tx = param.point.x - 210;
          if (ty < 0) ty = param.point.y + 10;
          if (ty + tooltipHeight > rect.height) ty = rect.height - tooltipHeight - 10;
        }
        const timeLabel = timeLabels.length > 0 ? findClosestTimeLabel(timeLabels, param.time!) : undefined;
        setTooltip({ visible: hasAny, x: tx, y: ty, time: param.time, timeLabel, values });
      };
      crosshairHandlerRef.current = handleCrosshairMove as any;
      chart.subscribeCrosshairMove(handleCrosshairMove as any);
      const onMouseLeave = () => setTooltip((t) => ({ ...t, visible: false }));
      mouseLeaveRef.current = onMouseLeave;
      container.addEventListener('mouseleave', onMouseLeave);

      try {
        chart.timeScale().setVisibleRange(effectiveRange as any);
      } catch {
        // ignore
      }
      requestAnimationFrame(() => {
        if (cancelled || !chartRef.current) return;
        try {
          chartRef.current!.timeScale().setVisibleRange(effectiveRange as any);
        } catch {
          // ignore
        }
      });
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      const chart = chartRef.current;
      if (chart && crosshairHandlerRef.current) {
        try {
          chart.unsubscribeCrosshairMove(crosshairHandlerRef.current);
        } catch {
          // ignore
        }
      }
      if (container && mouseLeaveRef.current) {
        container.removeEventListener('mouseleave', mouseLeaveRef.current);
      }
      if (chart) {
        chart.remove();
        chartRef.current = null;
        seriesRefs.current = [];
        anchorSeriesRef.current = null;
      }
    };
  // effectiveRange 제외: 참조가 바뀔 때마다 차트를 파괴/재생성하면 깜빡임 발생. 범위 갱신은 아래 data/range effect에서 처리.
  }, [columnIds, height, allSeriesData, effectiveRange, visibleTimeRange, axisAnchorData, columnLabelGetter, timeLabels]);

  useEffect(() => {
    allSeriesDataRef.current = allSeriesData;
    columnIdsRef.current = columnIds;
  }, [allSeriesData, columnIds]);

  useEffect(() => {
    if (!chartRef.current || !seriesRefs.current.length) return;
    if (anchorSeriesRef.current && axisAnchorData.length > 0) {
      anchorSeriesRef.current.setData(axisAnchorData as any);
    }
    allSeriesData.forEach((data, idx) => {
      const sr = seriesRefs.current[idx];
      if (sr && data.length > 0) {
        logIfTimeNotStrictlyAscending(`MultiColumnChart update series ${idx}`, data);
        sr.setData(data);
      }
    });
    try {
      chartRef.current!.timeScale().setVisibleRange(effectiveRange as any);
    } catch {
      // ignore
    }
    const raf1 = requestAnimationFrame(() => {
      if (!chartRef.current) return;
      try {
        chartRef.current.timeScale().setVisibleRange(effectiveRange as any);
      } catch {
        // ignore
      }
    });
    return () => cancelAnimationFrame(raf1);
  }, [allSeriesData, visibleTimeRange, effectiveRange, axisAnchorData]);

  // 크기 변경 시에만 applyOptions. 의존성 없이 매 렌더마다 실행되면 applyOptions/setVisibleRange가 매번 호출되어 깜빡임(특히 ka10095 다수 섹션)
  useEffect(() => {
    if (!chartRef.current || !containerRef.current) return;
    const w = Math.max(containerRef.current.offsetWidth || 400, 400);
    const h = Math.max(containerRef.current.clientHeight || height, height);
    const last = lastSizeRef.current;
    if (last && last.w === w && last.h === h) return;
    lastSizeRef.current = { w, h };
    chartRef.current.applyOptions({ width: w, height: h });
  });

  const hasData = allSeriesData.some((d) => d.length > 0);
  const chartAreaStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 320,
    minHeight: height,
    height: `${height}px`,
    position: 'relative',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
  };
  if (!hasData) {
    return (
      <div style={chartAreaStyle}>
        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#6b7280' }}>데이터가 없습니다</div>
      </div>
    );
  }
  return (
    <div style={chartAreaStyle}>
      <div ref={containerRef} style={{ width: '100%', height: '100%', minWidth: 320, minHeight: height, position: 'absolute', left: 0, top: 0 }} />
      {tooltip.visible && tooltip.values.length > 0 && (
        <div style={{ position: 'absolute', left: tooltip.x, top: tooltip.y, backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white', padding: '8px', borderRadius: '4px', fontSize: '12px', zIndex: 1000, pointerEvents: 'none', maxWidth: '220px', maxHeight: '320px', overflowY: 'auto' }}>
          <ChartTooltipContent time={tooltip.time} timeLabel={tooltip.timeLabel} values={tooltip.values} />
        </div>
      )}
    </div>
  );
};

const MultiColumnChart = React.memo(MultiColumnChartInner);

/** ka10027 칼럼 라벨 (0d 테이블처럼 칼럼명 + 설명) */
export const KA10027_COLUMN_LABELS: Record<string, string> = {
  tick_id: '틱ID',
  collected_at: '수집시각',
  stk_cls: '종목분류',
  stk_cd: '종목코드',
  stk_nm: '종목명',
  cur_prc: '현재가',
  pred_pre_sig: '전일대비기호',
  pred_pre: '전일대비',
  flu_rt: '등락률',
  sel_req: '매도잔량',
  buy_req: '매수잔량',
  now_trde_qty: '현재거래량',
  cntr_str: '체결강도',
  cnt: '횟수',
};

function getKa10027ColumnLabel(key: string): string {
  return KA10027_COLUMN_LABELS[key] ? `${key} ${KA10027_COLUMN_LABELS[key]}` : key;
}
function getKa10027ColumnLabelOnly(key: string): string {
  return KA10027_COLUMN_LABELS[key] ?? key;
}

/** 0c 테이블 칼럼 라벨 */
export const OC_COLUMN_LABELS: Record<string, string> = {
  '27': '(최우선)매도호가',
  '28': '(최우선)매수호가',
};

/** 0c 차트에 쓸 컬럼 ID 순서 (각각 섹션으로 표시) */
export const OC_CHART_COLUMN_IDS: string[] = ['27', '28'];

/** 1h 테이블 칼럼 라벨 */
export const OH1_COLUMN_LABELS: Record<string, string> = {
  '9001': '종목코드',
  '302': '종목명',
  '13': '누적거래량',
  '14': '누적거래대금',
  '9068': 'VI발동구분',
  '9008': 'KOSPI,KOSDAQ,전체구분',
  '9075': '장전구분',
  '1221': 'VI발동가격',
  '1223': '매매체결처리시각',
  '1224': 'VI해제시각',
  '1225': 'VI적용구분',
  '1236': '기준가격 정적',
  '1237': '기준가격 동적',
  '1238': '괴리율 정적',
  '1239': '괴리율 동적',
  '1489': 'VI발동가 등락율',
  '1490': 'VI발동횟수',
  '9069': '발동방향구분',
  '1279': 'Extra Item',
  name: 'name',
  type: 'type',
  item: 'item',
};

/** 1h 차트에 쓸 컬럼 ID 순서 (각각 섹션으로 표시) */
/** 1h 차트에 쓸 컬럼 ID (9001 종목코드, 302 종목명 제외) */
export const OH1_CHART_COLUMN_IDS: string[] = [
  '13', '14', '9068', '9075', '1221', '1223', '1224',
  '1489', '1490', '9069',
  // 9008(KOSPI,KOSDAQ,전체구분), 1236, 1237, 1238, 1239 제외
];

function getOcColumnLabel(key: string): string {
  return OC_COLUMN_LABELS[key] ? `${key} ${OC_COLUMN_LABELS[key]}` : key;
}
function getOcColumnLabelOnly(key: string): string {
  return OC_COLUMN_LABELS[key] ?? key;
}

function getOh1ColumnLabel(key: string): string {
  return OH1_COLUMN_LABELS[key] ? `${key} ${OH1_COLUMN_LABELS[key]}` : key;
}
function getOh1ColumnLabelOnly(key: string): string {
  return OH1_COLUMN_LABELS[key] ?? key;
}

/** ka10095 테이블 칼럼 라벨 */
export const KA10095_COLUMN_LABELS: Record<string, string> = {
  stk_cd: '종목코드',
  stk_nm: '종목명',
  cur_prc: '현재가',
  base_pric: '기준가',
  pred_pre: '전일대비',
  pred_pre_sig: '전일대비기호',
  flu_rt: '등락율',
  trde_qty: '거래량',
  trde_prica: '거래대금',
  cntr_qty: '체결량',
  cntr_str: '체결강도',
  pred_trde_qty_pre: '전일거래량대비',
  sel_bid: '매도호가',
  buy_bid: '매수호가',
  sel_1th_bid: '매도1차호가',
  sel_2th_bid: '매도2차호가',
  sel_3th_bid: '매도3차호가',
  sel_4th_bid: '매도4차호가',
  sel_5th_bid: '매도5차호가',
  buy_1th_bid: '매수1차호가',
  buy_2th_bid: '매수2차호가',
  buy_3th_bid: '매수3차호가',
  buy_4th_bid: '매수4차호가',
  buy_5th_bid: '매수5차호가',
  upl_pric: '상한가',
  lst_pric: '하한가',
  open_pric: '시가',
  high_pric: '고가',
  low_pric: '저가',
  close_pric: '종가',
  cntr_tm: '체결시간',
  exp_cntr_pric: '예상체결가',
  exp_cntr_qty: '예상체결량',
  cap: '자본금',
  fav: '액면가',
  mac: '시가총액',
  stkcnt: '주식수',
  bid_tm: '호가시간',
  dt: '일자',
  pri_sel_req: '우선매도잔량',
  pri_buy_req: '우선매수잔량',
  pri_sel_cnt: '우선매도건수',
  pri_buy_cnt: '우선매수건수',
  tot_sel_req: '총매도잔량',
  tot_buy_req: '총매수잔량',
  tot_sel_cnt: '총매도건수',
  tot_buy_cnt: '총매수건수',
  prty: '패리티',
  gear: '기어링',
  pl_qutr: '손익분기',
  cap_support: '자본지지',
  elwexec_pric: 'ELW행사가',
  cnvt_rt: '전환비율',
  elwexpr_dt: 'ELW만기일',
  cntr_engg: '미결제약정',
  cntr_pred_pre: '미결제전일대비',
  theory_pric: '이론가',
  innr_vltl: '내재변동성',
  delta: '델타',
  gam: '감마',
  theta: '쎄타',
  vega: '베가',
  law: '로',
};

/** ka10095 차트에 쓸 컬럼 ID 순서 (통합 섹션 제외한 나머지만; ohlc/bids/pri-jaeryang은 별도 통합 섹션) */
export const KA10095_CHART_COLUMN_IDS: string[] = [
  'trde_prica', 'cntr_qty',
  // exp_cntr_pric, exp_cntr_qty, pred_trde_qty_pre, fav, upl_pric, lst_pric 제외. upl_pric, lst_pric → ka10095-upl-lst. tot_sel_req, tot_buy_req → ka10095-tot-jaeryang(총매도·총매수잔량 통합). 그 외 제외: cur_prc, base_pric, pred_pre, pred_pre_sig, trde_qty, flu_rt, cntr_str, cntr_tm, bid_tm. sel_bid, buy_bid → ka10095-bids / open_pric~close_pric → ka10095-ohlc / pri_sel_req, pri_buy_req → ka10095-pri-jaeryang
  // 이하 ka10095 파생 지표(tot_sel_cnt, tot_buy_cnt, prty, gear, pl_qutr, cap_support, elwexec_pric, cnvt_rt, elwexpr_dt, cntr_engg, cntr_pred_pre, theory_pric, innr_vltl, delta, gam, theta, vega, law)는
  // 차트가 필요 없으므로 차트 대상 ID 배열에서 제외
];

/** ka10095 매도1~5차호가, 매수1~5차호가 통합 멀티차트용 컬럼 ID */
const KA10095_SEL_BID_COLUMN_IDS = ['sel_1th_bid', 'sel_2th_bid', 'sel_3th_bid', 'sel_4th_bid', 'sel_5th_bid'];
const KA10095_BUY_BID_COLUMN_IDS = ['buy_1th_bid', 'buy_2th_bid', 'buy_3th_bid', 'buy_4th_bid', 'buy_5th_bid'];

function getKa10095ColumnLabel(key: string): string {
  return KA10095_COLUMN_LABELS[key] ? `${key} ${KA10095_COLUMN_LABELS[key]}` : key;
}
function getKa10095ColumnLabelOnly(key: string): string {
  return KA10095_COLUMN_LABELS[key] ?? key;
}

/** 테이블 셀 표시용: 숫자는 소수점 이하 6자리, 객체/배열이면 JSON, 아니면 String */
function cellValueToString(v: unknown): string {
  if (v == null) return '-';
  if (typeof v === 'number') return Number.isNaN(v) ? '-' : v.toFixed(6);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** 1h data 칼럼: 배열 [{ name, type, item, values }] 파싱 후 가독성 있게 표시 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function formatDataColumnDisplay(raw: string): string {
  if (raw == null || raw === '-') return '-';
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return raw;
    const lines: string[] = [];
    for (const el of arr) {
      if (el && typeof el === 'object') {
        const name = el.name ?? '';
        const type = el.type ?? '';
        const item = el.item ?? '';
        const values = el.values;
        let line = `• ${name} (${type}) item=${item}`;
        if (values && typeof values === 'object' && !Array.isArray(values)) {
          const pairs = Object.entries(values)
            .map(([k, val]) => `${k}=${val}`)
            .join(', ');
          if (pairs) line += `\n  ${pairs}`;
        }
        lines.push(line);
      }
    }
    return lines.join('\n\n') || raw;
  } catch {
    return raw;
  }
}

/** ka10027/0c/ka10095 테이블: 데이터에서 모든 키를 수집해 칼럼으로 표시. 대량 시 앞쪽만 샘플링 */
function getKa10027ColumnKeys(events: Ka10027Row[]): string[] {
  const set = new Set<string>();
  const limit = Math.min(events.length, COLUMN_SAMPLE_SIZE);
  for (let i = 0; i < limit; i++) {
    const d = events[i].data && typeof events[i].data === 'object' ? events[i].data : {};
    Object.keys(d).forEach((k) => set.add(k));
  }
  const hasTickId = set.has('tick_id');
  const rest = Array.from(set)
    .filter((k) => k !== 'collected_at' && k !== 'tick_id')
    .sort();
  return hasTickId ? ['collected_at', ...rest, 'tick_id'] : ['collected_at', ...rest];
}

/** 1h 테이블 전용: data[] 배열 첫 요소의 values 키(13, 14, 302 등) + name, type, item 을 칼럼으로 수집. 대량 시 샘플링 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getOh1ColumnKeys(events: Ka10027Row[]): string[] {
  const valueKeys = new Set<string>();
  const limit = Math.min(events.length, COLUMN_SAMPLE_SIZE);
  for (let i = 0; i < limit; i++) {
    const ev = events[i];
    const dataArr = ev.data?.data;
    if (!Array.isArray(dataArr) || dataArr.length === 0) continue;
    const first = dataArr[0];
    const values = first?.values && typeof first.values === 'object' ? first.values : {};
    Object.keys(values).forEach((k) => valueKeys.add(k));
  }
  const rest = Array.from(valueKeys).sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });
  return ['collected_at', ...rest];
}

/** 1h 테이블: Redis 섹션 데이터가 flat event.data(키: 9068, 1238 등)일 때 컬럼 키 수집 */
function getOh1ColumnKeysFromFlat(events: Orderbook0dEvent[]): string[] {
  const valueKeys = new Set<string>();
  const limit = Math.min(events.length, COLUMN_SAMPLE_SIZE);
  for (let i = 0; i < limit; i++) {
    const d = events[i].data && typeof events[i].data === 'object' ? events[i].data : {};
    Object.keys(d).forEach((k) => valueKeys.add(k));
  }
  const rest = Array.from(valueKeys).sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });
  return ['collected_at', ...rest];
}

/** 1h 테이블: flat event.data로 행 생성 (Redis 섹션별 저장 형식) */
function buildOh1RowsFromFlat(
  events: Orderbook0dEvent[],
  columnKeys: string[],
  maxRows: number
): Array<Record<string, string> & { t?: number; key?: string }> {
  const sorted = takeMinRowsByT(events, maxRows);
  return sorted.map((ev, i) => {
    const row: Record<string, string> & { t?: number; key?: string } = {};
    row.key = `oh1-${i}`;
    row.t = ev.t;
    row.collected_at = ev.t ? formatTimeWithMicros(ev.t) : '-';
    const d = ev.data && typeof ev.data === 'object' ? ev.data : {};
    columnKeys.forEach((key) => {
      if (key === 'collected_at') return;
      const v = d[key];
      row[key] = v != null ? String(v) : '-';
    });
    if (i > 0) {
      const prev = sorted[i - 1].data && typeof sorted[i - 1].data === 'object' ? sorted[i - 1].data : {};
      columnKeys.forEach((key) => {
        if (key === 'collected_at') return;
        const currNum = toNum(d[key]);
        const prevNum = toNum(prev[key]);
        row[`${key}_delta`] = currNum != null ? formatDelta(prevNum, currNum) : '-';
      });
    }
    return row;
  });
}

/** 1h 테이블 전용: data[0]을 펼쳐서 행 생성 (칼럼: collected_at, name, type, item, 13, 14, 302 ...). 상위 maxRows개만 선택 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function buildOh1Rows(
  events: Ka10027Row[],
  columnKeys: string[],
  maxRows: number
): Array<Record<string, string> & { t?: number; key?: string }> {
  const sorted = takeMinRowsByT(events, maxRows);
  return sorted.map((ev, i) => {
    const row: Record<string, string> & { t?: number; key?: string } = {};
    row.key = `oh1-${i}`;
    row.t = ev.t;
    row.collected_at = (ev.data?.collected_at != null ? String(ev.data.collected_at) : ev.t ? formatTimeWithMicros(ev.t) : '-');
    const first = Array.isArray(ev.data?.data) && ev.data.data[0] ? ev.data.data[0] : null;
    if (first) {
      row.name = first.name != null ? String(first.name) : '-';
      row.type = first.type != null ? String(first.type) : '-';
      row.item = first.item != null ? String(first.item) : '-';
      const values = first.values && typeof first.values === 'object' ? first.values : {};
      Object.keys(values).forEach((k) => { row[k] = values[k] != null ? String(values[k]) : '-'; });
    }
    columnKeys.forEach((key) => {
      if (key === 'collected_at' || row[key] !== undefined) return;
      row[key] = '-';
    });
    if (i > 0) {
      const prevData = sorted[i - 1].data as { data?: Array<{ values?: Record<string, unknown> }> } | undefined;
      const prevArr = prevData?.data;
      const prevFirst = Array.isArray(prevArr) && prevArr[0] ? prevArr[0] : null;
      const prevValues: Record<string, unknown> = prevFirst?.values && typeof prevFirst.values === 'object' ? prevFirst.values : {};
      const currValues: Record<string, unknown> = first?.values && typeof first.values === 'object' ? first.values : {};
      columnKeys.forEach((key) => {
        if (key === 'collected_at' || key === 'name' || key === 'type' || key === 'item') return;
        const currNum = toNum(currValues[key]);
        const prevNum = toNum(prevValues[key]);
        row[`${key}_delta`] = currNum != null ? formatDelta(prevNum, currNum) : '-';
      });
    }
    return row;
  });
}

/** 시간(t) 기준 상위 maxRows개만 선택 후 정렬. 전체 배열 정렬로 인한 무거운 연산 방지 */
function takeMinRowsByT<T extends { t: number }>(events: T[], maxRows: number): T[] {
  if (events.length <= maxRows) return [...events].sort((a, b) => a.t - b.t);
  const kept: T[] = [];
  for (const ev of events) {
    if (kept.length < maxRows) {
      kept.push(ev);
      if (kept.length === maxRows) kept.sort((a, b) => a.t - b.t);
      continue;
    }
    if (ev.t < kept[kept.length - 1].t) {
      kept[kept.length - 1] = ev;
      kept.sort((a, b) => a.t - b.t);
    }
  }
  return kept.length < maxRows ? kept.sort((a, b) => a.t - b.t) : kept;
}

/** 같은 시간(t)을 가지는 0d 이벤트들을 하나의 행으로 병합 (섹션별 부분 데이터 → 전체 컬럼으로 통합) */
function mergeEventsByTime(events: Orderbook0dEvent[]): Orderbook0dEvent[] {
  if (events.length === 0) return [];
  const byTime = new Map<number, Orderbook0dEvent>();
  for (const ev of events) {
    const key = ev.t;
    const existing = byTime.get(key);
    const newData = (ev.data && typeof ev.data === 'object' ? (ev.data as Record<string, unknown>) : {}) ?? {};
    if (!existing) {
      byTime.set(key, {
        ...ev,
        data: { ...newData },
      });
    } else {
      const prevData = (existing.data && typeof existing.data === 'object' ? (existing.data as Record<string, unknown>) : {}) ?? {};
      byTime.set(key, {
        ...existing,
        data: { ...prevData, ...newData },
      });
    }
  }
  return Array.from(byTime.values()).sort((a, b) => a.t - b.t);
}

/** 테이블 공통: events에서 컬럼 키 수집 후 행 배열 생성 (변동 포함). 상위 maxRows개만 선택 후 연산 */
function buildTableRowsWithDeltas(
  events: Ka10027Row[],
  columnKeys: string[],
  maxRows: number
): Array<Record<string, string> & { t?: number; key?: string }> {
  const sorted = takeMinRowsByT(events, maxRows);
  return sorted.map((ev, i) => {
    const row: Record<string, string> & { t?: number; key?: string } = {};
    const d = ev.data || {};
    row.key = `row-${i}`;
    row.t = ev.t;
    row.collected_at =
      d['collected_at'] != null && d['collected_at'] !== ''
        ? cellValueToString(d['collected_at'])
        : ev.t
          ? formatTimeWithMicros(ev.t)
          : '-';
    columnKeys.forEach((key) => {
      if (key === 'collected_at') return;
      const v = d[key];
      row[key] = cellValueToString(v);
    });
    if (i > 0) {
      const prev = sorted[i - 1].data || {};
      columnKeys.forEach((key) => {
        if (key === 'collected_at') return;
        const currNum = toNum(ev.data?.[key]);
        const prevNum = toNum(prev[key]);
        row[`${key}_delta`] = currNum != null ? formatDelta(prevNum, currNum) : '-';
      });
    }
    return row;
  });
}

/** 차트 섹션 드래그 래퍼: 행 전체 드래그로 순서 변경 (닫힌 상태에서도 동작) */
const DraggableChartSection: React.FC<{
  sectionId: string;
  onReorder: (draggedId: string, dropTargetId: string) => void;
  children: React.ReactNode;
}> = ({ sectionId, onReorder, children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', sectionId);
    e.dataTransfer.effectAllowed = 'move';
    if (wrapperRef.current) wrapperRef.current.style.opacity = '0.5';
  };
  const handleDragEnd = () => {
    if (wrapperRef.current) wrapperRef.current.style.opacity = '';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData('text/plain');
    if (draggedId && draggedId !== sectionId) onReorder(draggedId, sectionId);
  };
  return (
    <div
      ref={wrapperRef}
      data-draggable-section
      data-section-id={sectionId}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{ display: 'flex', alignItems: 'stretch', width: '100%', minWidth: 0 }}
    >
      <div
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        style={{ padding: '8px 4px', display: 'flex', alignItems: 'center', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRight: 'none', borderTopLeftRadius: 8, borderBottomLeftRadius: 8, userSelect: 'none', cursor: 'grab' }}
        title="드래그하여 순서 변경"
      >
        <span style={{ fontSize: 14, color: '#6b7280' }}>⋮⋮</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
};

/** 차트 아코디언: 탭 헤더(클릭 시 열기/닫기) + 내용은 열린 경우 한 프레임 지연 마운트 (unresponsive 방지). 열릴 때 onOpen 호출로 해당 섹션 데이터 로드 요청 */
const ChartSection: React.FC<{
  sectionId: string;
  title: string;
  open: boolean;
  onToggle: () => void;
  onOpen?: () => void;
  chartHeight: number;
  children: React.ReactNode;
}> = ({ sectionId, title, open, onToggle, onOpen, chartHeight, children }) => {
  const [deferredShow, setDeferredShow] = useState(false);
  const openRequestedRef = React.useRef(false);
  const onOpenRef = React.useRef(onOpen);
  onOpenRef.current = onOpen;
  useEffect(() => {
    if (!open) {
      setDeferredShow(false);
      openRequestedRef.current = false;
      return;
    }
    if (!openRequestedRef.current) {
      openRequestedRef.current = true;
      onOpenRef.current?.();
    }
    let timeoutId: ReturnType<typeof setTimeout>;
    const raf = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => setDeferredShow(true), 0);
    });
    return () => {
      cancelAnimationFrame(raf);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [open]);

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff', width: '100%' }}>
      <div role="button" tabIndex={0} onClick={onToggle} onKeyDown={(e) => e.key === 'Enter' && onToggle()} style={{ padding: '8px 12px', fontWeight: 600, fontSize: 14, color: '#1f2937', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: open ? '1px solid #e5e7eb' : 'none' }}>
        <span>{title}</span>
        <span style={{ fontSize: 12 }}>{open ? '▼' : '▶'}</span>
      </div>
      {open && (
        <div style={{ padding: 12, width: '100%', boxSizing: 'border-box', minHeight: chartHeight, minWidth: 0, overflowX: 'auto', overflowY: 'visible' }}>
          {deferredShow ? (
            <div style={{ minWidth: 1200, width: 'max-content' }}>{children}</div>
          ) : (
            <div style={{ height: chartHeight, minWidth: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', color: '#6b7280', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6 }}>차트 준비 중…</div>
          )}
        </div>
      )}
    </div>
  );
};

/** ms → UTC datetime "YYYY-MM-DD HH:mm:ss.000000" (테이블 기간은 UTC만 사용, 소수점 이하 6자리) */
function msToDatetimeUTC(ms: number): string {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const M = String(d.getUTCMonth() + 1).padStart(2, '0');
  const D = String(d.getUTCDate()).padStart(2, '0');
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  const s = String(d.getUTCSeconds()).padStart(2, '0');
  const frac = String((ms % 1000) * 1000).padStart(6, '0');
  return `${y}-${M}-${D} ${h}:${m}:${s}.${frac}`;
}

/** 문자열(UTC) "YYYY-MM-DD HH:mm:ss.000000" → ms. 입력에 타임존 없으면 UTC로 해석 */
function parseDatetimeUTCToMs(s: string): number | null {
  const trimmed = s.trim();
  if (!trimmed) return null;
  const dotIdx = trimmed.lastIndexOf('.');
  let basePart: string;
  let fracMs = 0;
  if (dotIdx >= 0 && /^\d{1,6}$/.test(trimmed.slice(dotIdx + 1))) {
    const fracStr = trimmed.slice(dotIdx + 1).padEnd(6, '0').slice(0, 6);
    fracMs = Math.floor(parseInt(fracStr, 10) / 1000);
    basePart = trimmed.slice(0, dotIdx);
  } else {
    basePart = trimmed;
  }
  const normalized = basePart.includes('T') ? basePart : basePart.replace(' ', 'T');
  const withTz = /[Z+-]\d{2}:?\d{2}$/.test(normalized) ? normalized : `${normalized}Z`;
  const baseMs = Date.parse(withTz);
  if (Number.isNaN(baseMs)) return null;
  return baseMs + fracMs;
}

const Orderbook0dColumnCharts = ({
  baseKey,
  sectionData,
  btDetailTrades = null,
  onNeedSection,
  onNeedSections,
  chartHeight = 400,
  maxRows = 50,
  error = null,
  allowedTimeRange = null,
}: Orderbook0dColumnChartsProps) => {
  /** 테이블 서브기간: 입력값(문자열) */
  const [tableStartInput, setTableStartInput] = useState('');
  const [tableEndInput, setTableEndInput] = useState('');
  /** 테이블 갱신 시 적용된 기간(ms). null이면 갱신 전 → 테이블에 기간 내 데이터만 표시하지 않음(메시지 표시) */
  const [tableTimeRangeApplied, setTableTimeRangeApplied] = useState<{ from: number; to: number } | null>(null);
  const [tableRangeError, setTableRangeError] = useState<string | null>(null);

  /** 허용 기간(드롭다운)이 바뀌면 입력란 갱신. 적용된 테이블 기간은 기간 값이 실제로 바뀐 경우에만 초기화(참조만 바뀌면 유지) */
  useEffect(() => {
    if (!allowedTimeRange) {
      setTableStartInput('');
      setTableEndInput('');
      setTableTimeRangeApplied(null);
      setTableRangeError(null);
      return;
    }
    const from = allowedTimeRange.from;
    const to = allowedTimeRange.to;
    setTableStartInput(msToDatetimeUTC(from));
    setTableEndInput(msToDatetimeUTC(to));
    setTableRangeError(null);
    setTableTimeRangeApplied((prev) => {
      if (prev == null) return null;
      if (prev.from !== from || prev.to !== to) return null;
      return prev;
    });
  }, [allowedTimeRange?.from, allowedTimeRange?.to]); // eslint-disable-line react-hooks/exhaustive-deps -- 참조만 바뀌고 from/to 동일하면 테이블 적용 구간 유지

  const handleTableRefresh = useCallback(() => {
    setTableRangeError(null);
    if (!allowedTimeRange) {
      setTableRangeError('시간 범위를 먼저 선택해 주세요.');
      return;
    }
    const fromMs = parseDatetimeUTCToMs(tableStartInput);
    const toMs = parseDatetimeUTCToMs(tableEndInput);
    if (fromMs == null || toMs == null) {
      setTableRangeError('Start·End를 UTC 형식(예: YYYY-MM-DD HH:mm:ss.000000)으로 입력해 주세요.');
      return;
    }
    if (fromMs >= toMs) {
      setTableRangeError('Start는 End보다 이전이어야 합니다.');
      return;
    }
    const fromClamped = Math.max(fromMs, allowedTimeRange.from);
    const toClamped = Math.min(toMs, allowedTimeRange.to);
    if (fromClamped >= toClamped) {
      setTableRangeError('선택한 기간이 드롭다운 기간 안에 없습니다.');
      return;
    }
    setTableTimeRangeApplied({ from: fromClamped, to: toClamped });
  }, [allowedTimeRange, tableStartInput, tableEndInput]);

  const [openChartSectionIds, setOpenChartSectionIds] = useState<Set<string>>(
    () => new Set(['ka10027-flu_rt'])
  );
  const toggleChartSection = (id: string) => {
    setOpenChartSectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** 차트 시간축 범위(초). allowedTimeRange가 있으면 그 범주(전체 구간) 사용 → 축이 00:00:00부터 표시. 없으면 ka10027·0d 데이터 교집합 사용. */
  const sharedTimeRangeRef = useRef<{ from: number; to: number } | null>(null);
  const sharedTimeRange = useMemo((): { from: number; to: number } | null => {
    if (allowedTimeRange) {
      const from = allowedTimeRange.from / 1000;
      const to = allowedTimeRange.to / 1000;
      if (from < to && sharedTimeRangeRef.current?.from === from && sharedTimeRangeRef.current?.to === to)
        return sharedTimeRangeRef.current;
      sharedTimeRangeRef.current = { from, to };
      return sharedTimeRangeRef.current;
    }
    if (!sectionData || typeof sectionData !== 'object') {
      sharedTimeRangeRef.current = null;
      return null;
    }
    let ka27Min = Infinity;
    let ka27Max = -Infinity;
    let odMin = Infinity;
    let odMax = -Infinity;
    for (const id of Array.from(openChartSectionIds)) {
      const evs = sectionData[id] as Orderbook0dEvent[] | undefined;
      if (!Array.isArray(evs)) continue;
      for (const ev of evs) {
        const s = eventTimeSeconds(ev.t);
        if (id.startsWith('ka10027-')) {
          if (s < ka27Min) ka27Min = s;
          if (s > ka27Max) ka27Max = s;
        } else if (id.startsWith('0d-')) {
          if (s < odMin) odMin = s;
          if (s > odMax) odMax = s;
        }
      }
    }
    const fromK = ka27Min === Infinity ? null : ka27Min;
    const toK = ka27Max === -Infinity ? null : ka27Max;
    const fromD = odMin === Infinity ? null : odMin;
    const toD = odMax === -Infinity ? null : odMax;
    const from = fromK != null && fromD != null ? Math.max(fromK, fromD) : fromK ?? fromD;
    const to = toK != null && toD != null ? Math.min(toK, toD) : toK ?? toD;
    if (from == null || to == null || from >= to) {
      const fallback = fromK != null && toK != null ? { from: fromK, to: toK } : null;
      if (fallback && sharedTimeRangeRef.current && sharedTimeRangeRef.current.from === fallback.from && sharedTimeRangeRef.current.to === fallback.to)
        return sharedTimeRangeRef.current;
      sharedTimeRangeRef.current = fallback;
      return fallback;
    }
    if (sharedTimeRangeRef.current && sharedTimeRangeRef.current.from === from && sharedTimeRangeRef.current.to === to)
      return sharedTimeRangeRef.current;
    const next = { from, to };
    sharedTimeRangeRef.current = next;
    return next;
  }, [sectionData, openChartSectionIds, allowedTimeRange]);

  /** Redis 섹션별 데이터(sectionData)에서 테이블용 행·컬럼키·mergedRows 계산 (타입별 대표 섹션 1개 사용) */
  type TableDerived = {
    ka10027ColumnKeys: string[];
    odColumnKeys: string[];
    odDataKeys: string[];
    ocColumnKeys: string[];
    oh1ColumnKeys: string[];
    ka10095ColumnKeys: string[];
    ka10027Rows: Array<Record<string, string> & { t?: number }>;
    ocRows: Array<Record<string, string> & { t?: number; key?: string }>;
    oh1Rows: Array<Record<string, string> & { t?: number; key?: string }>;
    ka10095Rows: Array<Record<string, string> & { t?: number; key?: string }>;
    mergedRows: Array<{
      t: number;
      type: '0d' | 'ka10027' | '0c' | '1h' | 'ka10095';
      odRow?: { key: string; collected_at: string; cells: { delta: string; value: string }[] };
      ka27Row?: Record<string, string>;
      ocRow?: Record<string, string>;
      oh1Row?: Record<string, string>;
      ka10095Row?: Record<string, string>;
    }>;
  };
  const tableDerived = useMemo((): TableDerived => {
    const empty: TableDerived = {
      ka10027ColumnKeys: ['collected_at'],
      odColumnKeys: ['collected_at'],
      odDataKeys: [],
      ocColumnKeys: ['collected_at'],
      oh1ColumnKeys: ['collected_at'],
      ka10095ColumnKeys: ['collected_at'],
      ka10027Rows: [],
      ocRows: [],
      oh1Rows: [],
      ka10095Rows: [],
      mergedRows: [],
    };
    if (!sectionData || typeof sectionData !== 'object') return empty;
    /** 서브기간 미적용 시 테이블은 비움(갱신 버튼 후에만 표시) */
    if (!tableTimeRangeApplied) return empty;

    const collectMergedEvents = (prefix: string): Orderbook0dEvent[] => {
      const activeIds = Array.from(openChartSectionIds).filter((id) => id.startsWith(prefix));
      if (activeIds.length === 0) return [];
      const flat = activeIds.flatMap(
        (id) => (sectionData[id] as Orderbook0dEvent[] | undefined) ?? []
      ) as Orderbook0dEvent[];
      return mergeEventsByTime(flat);
    };

    /** 0c 테이블용: merge 없이 같은 collected_at의 모든 문서를 그대로 수집 (차트는 collectMergedEvents로 하나만 표시) */
    const collectOcEventsRaw = (): Orderbook0dEvent[] => {
      const activeIds = Array.from(openChartSectionIds).filter((id) => id.startsWith('0c-'));
      if (activeIds.length === 0) return [];
      return activeIds.flatMap(
        (id) => (sectionData[id] as Orderbook0dEvent[] | undefined) ?? []
      ) as Orderbook0dEvent[];
    };

    const fromMs = tableTimeRangeApplied.from;
    const toMs = tableTimeRangeApplied.to;
    const fromUs = fromMs * 1000;
    const toUs = toMs * 1000;
    const filterByTime = <T extends { t: number }>(arr: T[]): T[] =>
      arr.filter((ev) => ev.t >= fromUs && ev.t <= toUs);

    const odEvents = filterByTime(collectMergedEvents('0d-'));
    const ka10027Events = filterByTime(collectMergedEvents('ka10027-'));
    const ocEvents = filterByTime(collectOcEventsRaw());
    const oh1Events = filterByTime(collectMergedEvents('1h-'));
    const ka95Events = filterByTime(collectMergedEvents('ka10095-'));

    const rowLimit = MAX_TABLE_ROWS_WHEN_FILTERED;

    const ka10027AsKa = ka10027Events as Ka10027Row[];
    const ka10027ColumnKeys = ka10027Events.length > 0 ? getKa10027ColumnKeys(ka10027AsKa) : ['collected_at'];
    const ka10027Rows = ka10027Events.length > 0 ? buildTableRowsWithDeltas(ka10027AsKa, ka10027ColumnKeys, rowLimit) : [];

    const odAsKa = odEvents as Ka10027Row[];
    const odColumnKeys = odEvents.length > 0 ? getKa10027ColumnKeys(odAsKa) : ['collected_at'];
    const odRowsBuilt = odEvents.length > 0 ? buildTableRowsWithDeltas(odAsKa, odColumnKeys, rowLimit) : [];
    const odDataKeys = odColumnKeys.filter((k) => k !== 'collected_at');
    const odMergedRows: TableDerived['mergedRows'] = odRowsBuilt.map((row) => ({
      t: row.t ?? 0,
      type: '0d' as const,
      odRow: {
        key: row.key ?? '',
        collected_at: row.collected_at ?? '-',
        cells: odDataKeys.map((c) => ({ value: row[c] ?? '-', delta: row[`${c}_delta`] ?? '' })),
      },
    }));

    const ocAsKa = ocEvents as Ka10027Row[];
    const ocColumnKeys = ocEvents.length > 0 ? getKa10027ColumnKeys(ocAsKa) : ['collected_at'];
    const ocRows = ocEvents.length > 0 ? buildTableRowsWithDeltas(ocAsKa, ocColumnKeys, rowLimit) : [];

    const oh1ColumnKeys = oh1Events.length > 0 ? getOh1ColumnKeysFromFlat(oh1Events) : ['collected_at'];
    const oh1Rows = oh1Events.length > 0 ? buildOh1RowsFromFlat(oh1Events, oh1ColumnKeys, rowLimit) : [];

    const ka95AsKa = ka95Events as Ka10027Row[];
    const ka10095ColumnKeys = ka95Events.length > 0 ? getKa10027ColumnKeys(ka95AsKa) : ['collected_at'];
    const ka10095Rows = ka95Events.length > 0 ? buildTableRowsWithDeltas(ka95AsKa, ka10095ColumnKeys, rowLimit) : [];

    const ka27Merged: TableDerived['mergedRows'] = ka10027Rows.map((row) => ({ t: row.t ?? 0, type: 'ka10027' as const, ka27Row: row }));
    const ocMerged: TableDerived['mergedRows'] = ocRows.map((row) => ({ t: row.t ?? 0, type: '0c' as const, ocRow: row }));
    const oh1Merged: TableDerived['mergedRows'] = oh1Rows.map((row) => ({ t: row.t ?? 0, type: '1h' as const, oh1Row: row }));
    const ka95Merged: TableDerived['mergedRows'] = ka10095Rows.map((row) => ({ t: row.t ?? 0, type: 'ka10095' as const, ka10095Row: row }));

    const mergedRows = [...odMergedRows, ...ka27Merged, ...ocMerged, ...oh1Merged, ...ka95Merged].sort((a, b) => a.t - b.t);

    return {
      ka10027ColumnKeys,
      odColumnKeys,
      odDataKeys,
      ocColumnKeys,
      oh1ColumnKeys,
      ka10095ColumnKeys,
      ka10027Rows,
      ocRows,
      oh1Rows,
      ka10095Rows,
      mergedRows,
    };
  }, [sectionData, openChartSectionIds, tableTimeRangeApplied]);

  const ka10027ColumnKeys = tableDerived.ka10027ColumnKeys;
  const odColumnKeys = tableDerived.odColumnKeys;
  const odDataKeys = tableDerived.odDataKeys;
  const ocColumnKeys = tableDerived.ocColumnKeys;
  const oh1ColumnKeys = tableDerived.oh1ColumnKeys;
  const ka10095ColumnKeys = tableDerived.ka10095ColumnKeys;

  const odTableScrollRef = useRef<HTMLDivElement>(null);
  const kaTableScrollRef = useRef<HTMLDivElement>(null);
  const ocTableScrollRef = useRef<HTMLDivElement>(null);
  const oh1TableScrollRef = useRef<HTMLDivElement>(null);
  const ka10095TableScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollRefs = [kaTableScrollRef, odTableScrollRef, ocTableScrollRef, oh1TableScrollRef, ka10095TableScrollRef];
  const scrollSyncingRef = useRef(false);
  const syncScroll = (sourceRef: React.RefObject<HTMLDivElement | null>) => {
    if (scrollSyncingRef.current || !sourceRef.current) return;
    scrollSyncingRef.current = true;
    const top = sourceRef.current.scrollTop;
    tableScrollRefs.forEach((ref) => {
      if (ref.current && ref !== sourceRef) ref.current.scrollTop = top;
    });
    requestAnimationFrame(() => { scrollSyncingRef.current = false; });
  };

  type MergedRow = {
    t: number;
    type: '0d' | 'ka10027' | '0c' | '1h' | 'ka10095';
    odRow?: { key: string; collected_at: string; cells: { delta: string; value: string }[] };
    ka27Row?: Record<string, string>;
    ocRow?: Record<string, string>;
    oh1Row?: Record<string, string>;
    ka10095Row?: Record<string, string>;
  };
  const mergedRows: MergedRow[] = tableDerived.mergedRows;

  const [tableVisibleCount, setTableVisibleCount] = useState(INITIAL_TABLE_VISIBLE);
  const mergedRowsLengthRef = useRef(0);
  useEffect(() => {
    if (mergedRows.length !== mergedRowsLengthRef.current) {
      mergedRowsLengthRef.current = mergedRows.length;
      setTableVisibleCount(Math.min(INITIAL_TABLE_VISIBLE, mergedRows.length));
    }
  }, [mergedRows.length]);
  useEffect(() => {
    if (tableVisibleCount >= mergedRows.length) return;
    const id = setTimeout(() => {
      setTableVisibleCount((prev) => Math.min(prev + TABLE_BATCH_SIZE, mergedRows.length));
    }, 0);
    return () => clearTimeout(id);
  }, [tableVisibleCount, mergedRows.length]);

  const visibleMergedRows = mergedRows.slice(0, tableVisibleCount);

  /** 테이블별 데이터 존재 여부 (데이터 없을 때 thead/collected_at 미표시) */
  const hasKa10027TableData = mergedRows.some((mr) => mr.type === 'ka10027');
  const hasOdTableData = mergedRows.some((mr) => mr.type === '0d');
  const hasOcTableData = mergedRows.some((mr) => mr.type === '0c');
  const hasOh1TableData = mergedRows.some((mr) => mr.type === '1h');
  const hasKa10095TableData = mergedRows.some((mr) => mr.type === 'ka10095');

  /** baseKey가 설정되면(추출 성공 또는 저장된 키 클릭) 이미 열려 있는 차트 섹션들 데이터 즉시 로드 */
  useEffect(() => {
    if (!baseKey || !onNeedSection) return;
    const ids = Array.from(openChartSectionIds);
    onNeedSections?.(ids);
    ids.forEach((id) => onNeedSection(id));
  }, [baseKey]); // eslint-disable-line react-hooks/exhaustive-deps -- openChartSectionIds는 의도적으로 제외: baseKey 변경 시점의 열린 섹션만 로드

  /** 21 호가시간 차트 제거. 차트는 전체 컬럼 기준으로 모두 활성화 */
  const chartColumnsForCharts = OD_COLUMN_ORDER.filter((c) => c !== '21');
  const hasMaedoHoga = MAEDO_HOGA_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasMaedoHogaSuyang = MAEDO_HOGA_SUYANG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasMaedoHogaJikjeon = MAEDO_HOGA_JIKJEON_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasMasuHoga = MASU_HOGA_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasMasuHogaSuyang = MASU_HOGA_SUYANG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasMasuHogaJikjeon = MASU_HOGA_JIKJEON_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasKrxMaedoJaeryang = KRX_MAEDO_JAERYANG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasKrxMasuJaeryang = KRX_MASU_JAERYANG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasNxtMaedoJaeryang = NXT_MAEDO_JAERYANG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasNxtMasuJaeryang = NXT_MASU_JAERYANG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasHogaJaeryangJikjeon = HOGA_JAERYANG_JIKJEON_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasBiryu = BIRYU_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasSunmaesuJaeryang = SUNMAESU_JAERYANG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasMaedoMasuHogaJaeryang = MAEDO_MASU_HOGA_JAERYANG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasKrxJaeryang = KRX_JAERYANG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasKrxJaeryangJeunggam = KRX_JAERYANG_JEUNGGAM_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasKrxJaeryangTotal = KRX_JAERYANG_TOTAL_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasNxtJaeryang = NXT_JAERYANG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasNxtJaeryangJeunggam = NXT_JAERYANG_JEUNGGAM_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasNxtJaeryangJeunggamTotal = NXT_JAERYANG_JEUNGGAM_TOTAL_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasKrxNxtJoonggaSig = KRX_NXT_JOONGGA_SIG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasKrxNxtJoonggaDaebi = KRX_NXT_JOONGGA_DAEBI_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasKrxNxtJoongga = KRX_NXT_JOONGGA_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasExpectedCntr = EXPECTED_CNTR_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasExpectedCntrFluRt = EXPECTED_CNTR_FLU_RT_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasExpectedCntrSig = EXPECTED_CNTR_SIG_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasKrxNxtJoonggaFluRt = KRX_NXT_JOONGGA_FLU_RT_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasExpectedCntrPre = EXPECTED_CNTR_PRE_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const hasExpectedCntrPric = EXPECTED_CNTR_PRIC_COLUMN_IDS.some((c) => chartColumnsForCharts.includes(c));
  const singleChartColumns = chartColumnsForCharts.filter(
    (c) =>
      !MAEDO_HOGA_COLUMN_IDS.includes(c) &&
      !MAEDO_HOGA_SUYANG_COLUMN_IDS.includes(c) &&
      !MAEDO_HOGA_JIKJEON_COLUMN_IDS.includes(c) &&
      !MASU_HOGA_COLUMN_IDS.includes(c) &&
      !MASU_HOGA_SUYANG_COLUMN_IDS.includes(c) &&
      !MASU_HOGA_JIKJEON_COLUMN_IDS.includes(c) &&
      !KRX_MAEDO_JAERYANG_COLUMN_IDS.includes(c) &&
      !KRX_MASU_JAERYANG_COLUMN_IDS.includes(c) &&
      !NXT_MAEDO_JAERYANG_COLUMN_IDS.includes(c) &&
      !NXT_MASU_JAERYANG_COLUMN_IDS.includes(c) &&
      !HOGA_JAERYANG_JIKJEON_COLUMN_IDS.includes(c) &&
      !BIRYU_COLUMN_IDS.includes(c) &&
      !SUNMAESU_JAERYANG_COLUMN_IDS.includes(c) &&
      !MAEDO_MASU_HOGA_JAERYANG_COLUMN_IDS.includes(c) &&
      !KRX_JAERYANG_COLUMN_IDS.includes(c) &&
      !KRX_JAERYANG_JEUNGGAM_COLUMN_IDS.includes(c) &&
      !KRX_JAERYANG_TOTAL_COLUMN_IDS.includes(c) &&
      !NXT_JAERYANG_COLUMN_IDS.includes(c) &&
      !NXT_JAERYANG_JEUNGGAM_COLUMN_IDS.includes(c) &&
      !NXT_JAERYANG_JEUNGGAM_TOTAL_COLUMN_IDS.includes(c) &&
      !KRX_NXT_JOONGGA_SIG_COLUMN_IDS.includes(c) &&
      !KRX_NXT_JOONGGA_DAEBI_COLUMN_IDS.includes(c) &&
      !KRX_NXT_JOONGGA_COLUMN_IDS.includes(c) &&
      !EXPECTED_CNTR_COLUMN_IDS.includes(c) &&
      !EXPECTED_CNTR_FLU_RT_COLUMN_IDS.includes(c) &&
      !EXPECTED_CNTR_SIG_COLUMN_IDS.includes(c) &&
      !KRX_NXT_JOONGGA_FLU_RT_COLUMN_IDS.includes(c) &&
      !EXPECTED_CNTR_PRE_COLUMN_IDS.includes(c) &&
      !EXPECTED_CNTR_PRIC_COLUMN_IDS.includes(c) &&
      !FIXED_CHART_COLUMN_IDS.includes(c)
  );
  const singleChartDisplay = singleChartColumns;

  /** 차트 섹션 표시 순서 (드래그로 변경, localStorage에 저장) */
  const defaultSectionOrder = useMemo(() => {
    const fixed = CHART_SECTION_ORDER_FIXED;
    const beforeSingle = fixed.indexOf('0d-krx-nxt-joongga-flurt') + 1;
    const before0c = fixed.indexOf('0c-27-28');
    const beforeKa95 = fixed.indexOf('ka10095-sel-bids');
    const afterKa95Fixed = fixed.indexOf('ka10095-tot-jaeryang') + 1;
    return [
      ...fixed.slice(0, beforeSingle),
      ...FIXED_CHART_COLUMN_IDS.map((c) => `0d-fixed-${c}`),
      ...fixed.slice(beforeSingle, before0c),
      ...singleChartDisplay.map((c) => `0d-single-${c}`),
      ...fixed.slice(before0c, beforeKa95),
      ...OH1_CHART_COLUMN_IDS.map((c) => `1h-${c}`),
      ...fixed.slice(beforeKa95, afterKa95Fixed),
      ...KA10095_CHART_COLUMN_IDS.map((c) => `ka10095-${c}`),
    ];
  }, [singleChartDisplay]);

  const DEPRECATED_CHART_SECTION_IDS = useMemo(() => ['1h-1236-1237', '1h-1238-1239', 'ka10095-tot_sel_req', 'ka10095-tot_buy_req'], []);
  const [chartSectionOrder, setChartSectionOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('mercury_chart_section_order');
      if (saved) {
        const parsed = JSON.parse(saved) as string[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          const filtered = parsed.filter((id) => !DEPRECATED_CHART_SECTION_IDS.includes(id));
          return filtered.length > 0 ? filtered : [];
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  useEffect(() => {
    if (defaultSectionOrder.length === 0) return;
    setChartSectionOrder((prev) => {
      const cleaned = prev.filter((id) => !DEPRECATED_CHART_SECTION_IDS.includes(id));
      if (cleaned.length === 0) return defaultSectionOrder;
      const set = new Set(cleaned);
      const added = defaultSectionOrder.filter((id) => !set.has(id));
      if (added.length === 0) {
        if (prev.length === cleaned.length && prev.every((id, i) => id === cleaned[i])) return prev;
        return cleaned;
      }
      return [...cleaned, ...added];
    });
  }, [defaultSectionOrder, DEPRECATED_CHART_SECTION_IDS]);

  useEffect(() => {
    if (chartSectionOrder.length === 0) return;
    try {
      localStorage.setItem('mercury_chart_section_order', JSON.stringify(chartSectionOrder));
    } catch {
      // ignore
    }
  }, [chartSectionOrder]);

  /** 드래그로 순서 변경 시 사용 */
  const handleChartSectionReorder = (draggedId: string, dropTargetId: string) => {
    setChartSectionOrder((prev) => {
      const i = prev.indexOf(draggedId);
      const j = prev.indexOf(dropTargetId);
      if (i === -1 || j === -1 || i === j) return prev;
      const next = prev.slice();
      next.splice(i, 1);
      next.splice(next.indexOf(dropTargetId), 0, draggedId);
      return next;
    });
  };

  /** sectionId에 해당하는 차트 섹션 JSX 반환 (순서 변경 시 사용) */
  const renderChartSectionById = (sid: string): React.ReactNode => {
    const open = openChartSectionIds.has(sid);
    const toggle = () => toggleChartSection(sid);
    const onOpen = () => onNeedSection?.(sid);
    if (sid.startsWith('0d-single-')) {
      const colId = sid.replace('0d-single-', '');
      const evs = sectionData[sid] ?? [];
      return (
        <ChartSection sectionId={sid} title={`[0d] ${getColumnLabel(colId)}`} open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
          <ColumnChart columnId={colId} events={evs} height={chartHeight} visibleTimeRange={sharedTimeRange} />
        </ChartSection>
      );
    }
    if (sid.startsWith('1h-')) {
      const colId = sid.replace('1h-', '');
      const hasData = (sectionData[sid] ?? []).length > 0;
      return (
        <ChartSection sectionId={sid} title={`[1h] ${colId} ${getOh1ColumnLabelOnly(colId)}`} open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
          <MultiChartSectionContent sectionId={sid} hasFlag={hasData} sectionData={sectionData} columnIds={[colId]} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getOh1ColumnLabel} />
        </ChartSection>
      );
    }
    if (sid.startsWith('ka10095-') && !['ka10095-sel-bids', 'ka10095-buy-bids', 'ka10095-ohlc', 'ka10095-upl-lst', 'ka10095-bids', 'ka10095-pri-jaeryang', 'ka10095-tot-jaeryang'].includes(sid)) {
      const colId = sid.replace('ka10095-', '');
      const hasData = (sectionData[sid] ?? []).length > 0;
      return (
        <ChartSection sectionId={sid} title={`[ka10095] ${colId} ${getKa10095ColumnLabelOnly(colId)}`} open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
          <MultiChartSectionContent sectionId={sid} hasFlag={hasData} sectionData={sectionData} columnIds={[colId]} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getKa10095ColumnLabel} />
        </ChartSection>
      );
    }
    if (sid.startsWith('0d-fixed-')) {
      const colId = sid.replace('0d-fixed-', '');
      const evs = sectionData[sid] ?? [];
      return (
        <ChartSection sectionId={sid} title={`[0d] ${getColumnLabel(colId)}`} open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
          <ColumnChart columnId={colId} events={evs} height={chartHeight} visibleTimeRange={sharedTimeRange} />
        </ChartSection>
      );
    }
    switch (sid) {
      case 'ka10027-flu_rt':
        return (
          <ChartSection sectionId={sid} title="[ka10027] flu_rt (등락률)" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <Ka10027FluRtChart ka10027Events={(sectionData[sid] ?? []) as Ka10027Row[]} height={chartHeight} visibleTimeRange={sharedTimeRange} />
            {btDetailTrades && btDetailTrades.length > 0 && (
              <div style={{ marginTop: 12, padding: 8, fontSize: 12, border: '1px solid #dee2e6', borderRadius: 4, backgroundColor: '#f8f9fa', color: '#000' }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: '#000' }}>백테스트 매매 수치</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, color: '#000' }}>
                  {btDetailTrades.map((t, i) => {
                    const buyRate = t.buy_rate != null && t.buy_rate !== '' ? String(t.buy_rate) : '-';
                    const sellRate = t.sell_rate != null && t.sell_rate !== '' && Number(t.sell_rate) !== 0 ? String(t.sell_rate) : '-';
                    const buyTickId = t.buy_tick_id != null && t.buy_tick_id !== '' ? String(t.buy_tick_id) : '-';
                    const sellTickId = t.sell_tick_id != null && t.sell_tick_id !== '' ? String(t.sell_tick_id) : '-';
                    const profitRate = t.profit_rate != null && t.profit_rate !== '' ? (Number.isFinite(Number(t.profit_rate)) ? Number(t.profit_rate).toFixed(2) : String(t.profit_rate)) : '-';
                    return (
                      <div key={i} style={{ padding: 6, background: '#fff', border: '1px solid #e9ecef', borderRadius: 4, color: '#000' }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, color: '#495057' }}>
                          {t.displayRound != null ? `백테 ${t.displayRound}회차` : `#${i + 1}`}
                          {t.firstCreatedAt ? ` ${t.firstCreatedAt.replace('T', ' ').replace(/Z$/, '').slice(0, 19)}` : ''}
                        </div>
                        <div>매수등락률: {buyRate}</div>
                        <div>매도등락률: {sellRate}</div>
                        <div>매수시간: {buyTickId}</div>
                        <div>매도시간: {sellTickId}</div>
                        <div style={{ color: profitRate === '-' ? '#000' : Number(profitRate) >= 0 ? '#c0392b' : '#1565c0' }}>수익률: {profitRate}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </ChartSection>
        );
      case 'ka10027-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[ka10027] 매도잔량·매수잔량" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={(sectionData[sid] ?? []).length > 0} sectionData={sectionData} columnIds={['sel_req', 'buy_req']} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getKa10027ColumnLabel} />
          </ChartSection>
        );
      case 'ka10027-cntr_str':
      case 'ka10027-cur_prc':
      case 'ka10027-now_trde_qty':
      case 'ka10027-pred_pre': {
        const fieldKey = sid.replace('ka10027-', '');
        const evs = sectionData[sid];
        return (
          <ChartSection sectionId={sid} title={`[ka10027] ${fieldKey} (${getKa10027ColumnLabelOnly(fieldKey as 'cntr_str' | 'cur_prc' | 'now_trde_qty' | 'pred_pre')})`} open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <Ka10027FieldChart ka10027Events={(evs ?? []) as Ka10027Row[]} fieldKey={fieldKey as 'cntr_str' | 'cur_prc' | 'now_trde_qty' | 'pred_pre'} title="" height={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      }
      case '0d-maedo-hoga':
        return (
          <ChartSection sectionId={sid} title="[0d] 41 매도호가1 ~ 50 매도호가10" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasMaedoHoga} sectionData={sectionData} columnIds={MAEDO_HOGA_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-masu-hoga':
        return (
          <ChartSection sectionId={sid} title="[0d] 51 매수호가1 ~ 60 매수호가10" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasMasuHoga} sectionData={sectionData} columnIds={MASU_HOGA_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-expected-cntr-pric':
        return (
          <ChartSection sectionId={sid} title="[0d] 23 예상체결가, 291 예상체결가" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasExpectedCntrPric} sectionData={sectionData} columnIds={EXPECTED_CNTR_PRIC_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-expected-cntr':
        return (
          <ChartSection sectionId={sid} title="[0d] 24 예상체결수량, 292 예상체결량" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasExpectedCntr} sectionData={sectionData} columnIds={EXPECTED_CNTR_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-expected-cntr-flurt':
        return (
          <ChartSection sectionId={sid} title="[0d] 201, 295 예상체결가 등락율" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasExpectedCntrFluRt} sectionData={sectionData} columnIds={EXPECTED_CNTR_FLU_RT_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-expected-cntr-sig':
        return (
          <ChartSection sectionId={sid} title="[0d] 238, 293 예상체결가 기호" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasExpectedCntrSig} sectionData={sectionData} columnIds={EXPECTED_CNTR_SIG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-expected-cntr-pre':
        return (
          <ChartSection sectionId={sid} title="[0d] 294, 200 예상체결가 전일대비" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasExpectedCntrPre} sectionData={sectionData} columnIds={EXPECTED_CNTR_PRE_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-maedo-masu-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[0d] 121, 125 매도/매수호가총잔량" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasMaedoMasuHogaJaeryang} sectionData={sectionData} columnIds={MAEDO_MASU_HOGA_JAERYANG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-maedo-hoga-suyang':
        return (
          <ChartSection sectionId={sid} title="[0d] 61~70 매도호가수량1~10" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasMaedoHogaSuyang} sectionData={sectionData} columnIds={MAEDO_HOGA_SUYANG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-maedo-hoga-jikjeon':
        return (
          <ChartSection sectionId={sid} title="[0d] 81~90 매도호가직전대비1~10" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasMaedoHogaJikjeon} sectionData={sectionData} columnIds={MAEDO_HOGA_JIKJEON_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-masu-hoga-suyang':
        return (
          <ChartSection sectionId={sid} title="[0d] 71~80 매수호가수량1~10" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasMasuHogaSuyang} sectionData={sectionData} columnIds={MASU_HOGA_SUYANG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-masu-hoga-jikjeon':
        return (
          <ChartSection sectionId={sid} title="[0d] 91~100 매수호가직전대비1~10" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasMasuHogaJikjeon} sectionData={sectionData} columnIds={MASU_HOGA_JIKJEON_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-krx-maedo-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[0d] 6044~6053 KRX 매도호가잔량1~10" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasKrxMaedoJaeryang} sectionData={sectionData} columnIds={KRX_MAEDO_JAERYANG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-krx-masu-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[0d] 6054~6063 KRX 매수호가잔량1~10" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasKrxMasuJaeryang} sectionData={sectionData} columnIds={KRX_MASU_JAERYANG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-nxt-maedo-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[0d] 6066~6075 NXT 매도호가잔량1~10" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasNxtMaedoJaeryang} sectionData={sectionData} columnIds={NXT_MAEDO_JAERYANG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-nxt-masu-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[0d] 6076~6085 NXT 매수호가잔량1~10" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasNxtMasuJaeryang} sectionData={sectionData} columnIds={NXT_MASU_JAERYANG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-hoga-jaeryang-jikjeon':
        return (
          <ChartSection sectionId={sid} title="[0d] 122, 126 매도/매수호가총잔량직전대비" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasHogaJaeryangJikjeon} sectionData={sectionData} columnIds={HOGA_JAERYANG_JIKJEON_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-biryu':
        return (
          <ChartSection sectionId={sid} title="[0d] 129 매수비율, 139 매도비율" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasBiryu} sectionData={sectionData} columnIds={BIRYU_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-sunmaesu-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[0d] 128 순매수잔량, 138 순매도잔량" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasSunmaesuJaeryang} sectionData={sectionData} columnIds={SUNMAESU_JAERYANG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-krx-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[0d] 6064, 6065 KRX 매도/매수호가총잔량" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasKrxJaeryang} sectionData={sectionData} columnIds={KRX_JAERYANG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-krx-jaeryang-jeunggam':
        return (
          <ChartSection sectionId={sid} title="[0d] 6100, 6104 KRX 중간가 총잔량 증감" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasKrxJaeryangJeunggam} sectionData={sectionData} columnIds={KRX_JAERYANG_JEUNGGAM_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-krx-jaeryang-total':
        return (
          <ChartSection sectionId={sid} title="[0d] 6101, 6103 KRX 중간가 매도/매수 총잔량" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasKrxJaeryangTotal} sectionData={sectionData} columnIds={KRX_JAERYANG_TOTAL_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-nxt-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[0d] 6086, 6087 NXT 매도/매수호가총잔량" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasNxtJaeryang} sectionData={sectionData} columnIds={NXT_JAERYANG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-nxt-jaeryang-jeunggam':
        return (
          <ChartSection sectionId={sid} title="[0d] 6105, 6109 NXT중간가 총잔량 증감" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasNxtJaeryangJeunggam} sectionData={sectionData} columnIds={NXT_JAERYANG_JEUNGGAM_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-nxt-jaeryang-total':
        return (
          <ChartSection sectionId={sid} title="[0d] 6106, 6108 NXT중간가 매도/매수 총잔량" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasNxtJaeryangJeunggamTotal} sectionData={sectionData} columnIds={NXT_JAERYANG_JEUNGGAM_TOTAL_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-krx-nxt-joongga-sig':
        return (
          <ChartSection sectionId={sid} title="[0d] 6111, 6114 KRX/NXT중간가대비 기호" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasKrxNxtJoonggaSig} sectionData={sectionData} columnIds={KRX_NXT_JOONGGA_SIG_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-krx-nxt-joongga-daebi':
        return (
          <ChartSection sectionId={sid} title="[0d] 6110, 6113 KRX/NXT중간가대비" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasKrxNxtJoonggaDaebi} sectionData={sectionData} columnIds={KRX_NXT_JOONGGA_DAEBI_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-krx-nxt-joongga':
        return (
          <ChartSection sectionId={sid} title="[0d] 6102, 6107 KRX/NXT 중간가" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasKrxNxtJoongga} sectionData={sectionData} columnIds={KRX_NXT_JOONGGA_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0d-krx-nxt-joongga-flurt':
        return (
          <ChartSection sectionId={sid} title="[0d] 6112, 6115 KRX/NXT중간가대비등락율" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={hasKrxNxtJoonggaFluRt} sectionData={sectionData} columnIds={KRX_NXT_JOONGGA_FLU_RT_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} />
          </ChartSection>
        );
      case '0c-27-28': {
        const ocRaw = (sectionData[sid] ?? []) as Orderbook0dEvent[];
        const ocMergedForChart = mergeEventsByTime(ocRaw);
        const sectionDataOcChart = { ...sectionData, [sid]: ocMergedForChart };
        return (
          <ChartSection sectionId={sid} title="[0c] 27, 28 (최우선)매도호가·매수호가" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={ocRaw.length > 0} sectionData={sectionDataOcChart} columnIds={OC_CHART_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getOcColumnLabel} />
          </ChartSection>
        );
      }
      case 'ka10095-sel-bids':
        return (
          <ChartSection sectionId={sid} title="[ka10095] 매도1~5차호가" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={(sectionData[sid] ?? []).length > 0} sectionData={sectionData} columnIds={KA10095_SEL_BID_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getKa10095ColumnLabel} />
          </ChartSection>
        );
      case 'ka10095-buy-bids':
        return (
          <ChartSection sectionId={sid} title="[ka10095] 매수1~5차호가" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={(sectionData[sid] ?? []).length > 0} sectionData={sectionData} columnIds={KA10095_BUY_BID_COLUMN_IDS} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getKa10095ColumnLabel} />
          </ChartSection>
        );
      case 'ka10095-ohlc':
        return (
          <ChartSection sectionId={sid} title="[ka10095] 시가·고가·저가·종가" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={(sectionData[sid] ?? []).length > 0} sectionData={sectionData} columnIds={['open_pric', 'high_pric', 'low_pric', 'close_pric']} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getKa10095ColumnLabel} />
          </ChartSection>
        );
      case 'ka10095-upl-lst':
        return (
          <ChartSection sectionId={sid} title="[ka10095] 상한가·하한가" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={(sectionData[sid] ?? []).length > 0} sectionData={sectionData} columnIds={['upl_pric', 'lst_pric']} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getKa10095ColumnLabel} />
          </ChartSection>
        );
      case 'ka10095-bids':
        return (
          <ChartSection sectionId={sid} title="[ka10095] 매도호가·매수호가" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={(sectionData[sid] ?? []).length > 0} sectionData={sectionData} columnIds={['sel_bid', 'buy_bid']} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getKa10095ColumnLabel} />
          </ChartSection>
        );
      case 'ka10095-pri-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[ka10095] 우선매도잔량·우선매수잔량" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={(sectionData[sid] ?? []).length > 0} sectionData={sectionData} columnIds={['pri_sel_req', 'pri_buy_req']} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getKa10095ColumnLabel} />
          </ChartSection>
        );
      case 'ka10095-tot-jaeryang':
        return (
          <ChartSection sectionId={sid} title="[ka10095] 총매도잔량·총매수잔량" open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <MultiChartSectionContent sectionId={sid} hasFlag={(sectionData[sid] ?? []).length > 0} sectionData={sectionData} columnIds={['tot_sel_req', 'tot_buy_req']} chartHeight={chartHeight} visibleTimeRange={sharedTimeRange} columnLabelGetter={getKa10095ColumnLabel} />
          </ChartSection>
        );
      default:
        return (
          <ChartSection sectionId={sid} title={sid} open={open} onToggle={toggle} onOpen={onOpen} chartHeight={chartHeight}>
            <div style={{ padding: 8, color: '#6b7280', fontSize: 13 }}>섹션: {sid}</div>
          </ChartSection>
        );
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
      {error && (
        <div style={{ padding: 12, color: '#dc2626', fontSize: 14, backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
          {error}
        </div>
      )}
      {baseKey == null && (
        <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>종목 버튼을 클릭한 뒤 차트 섹션을 열면 해당 차트 데이터만 조회됩니다.</p>
      )}
      {baseKey != null && (
        <p style={{ margin: 0, fontSize: 13, color: '#1f2937', backgroundColor: '#fff', padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb' }}>
          차트별 로드: 로드된 섹션 <strong>{Object.keys(sectionData).length}개</strong>
          {!SHOW_TABLES && ' · 시트(테이블) 일시 비표시'}
        </p>
      )}
      {/* 차트: 드래그 핸들(⋮⋮)로 순서 변경 가능 */}
      <div style={{ flex: '0 0 auto', width: '100%', minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {(chartSectionOrder.length > 0 ? chartSectionOrder : defaultSectionOrder)
          .filter((id) => !DEPRECATED_CHART_SECTION_IDS.includes(id))
          .map((sectionId) => (
          <DraggableChartSection key={sectionId} sectionId={sectionId} onReorder={handleChartSectionReorder}>
            {renderChartSectionById(sectionId)}
          </DraggableChartSection>
        ))}
      </div>

      {/* 시트: ka10027 | 0d | 0c | 1h | ka10095 가로 배치 (SHOW_TABLES=false 시 비표시) */}
      {SHOW_TABLES && (
      <div style={{ flex: '1 1 0%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {/* 테이블 서브기간: Start / End + 테이블 갱신 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>Start (UTC):</label>
          <input
            type="text"
            value={tableStartInput}
            onChange={(e) => setTableStartInput(e.target.value)}
            placeholder="YYYY-MM-DD HH:mm:ss.000000"
            title="UTC"
            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: 240, minWidth: 240, maxWidth: 240, boxSizing: 'border-box', flexShrink: 0 }}
          />
          <label style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>End (UTC):</label>
          <input
            type="text"
            value={tableEndInput}
            onChange={(e) => setTableEndInput(e.target.value)}
            placeholder="YYYY-MM-DD HH:mm:ss.000000"
            title="UTC"
            style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: 240, minWidth: 240, maxWidth: 240, boxSizing: 'border-box', flexShrink: 0 }}
          />
          <button
            type="button"
            onClick={handleTableRefresh}
            style={{
              padding: '6px 14px',
              fontWeight: 600,
              fontSize: 13,
              color: '#fff',
              backgroundColor: '#2563eb',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            테이블 갱신
          </button>
          {tableRangeError && (
            <span style={{ fontSize: 12, color: '#dc2626' }}>{tableRangeError}</span>
          )}
          {!allowedTimeRange && baseKey != null && (
            <span style={{ fontSize: 12, color: '#6b7280' }}>상단에서 시간 범위를 선택하면 테이블 기간을 설정할 수 있습니다.</span>
          )}
        </div>

        <div style={{ flex: '1 1 0%', minWidth: 0, display: 'flex', flexDirection: 'row', gap: 8, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {/* ka10027 테이블 */}
        <div style={{ flex: '1 1 0%', minWidth: 0, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, fontSize: 14, flex: '0 0 auto', color: '#1f2937', backgroundColor: '#fff' }}>
            ka10027
          </div>
          <div ref={kaTableScrollRef} onScroll={() => syncScroll(kaTableScrollRef)} style={{ flex: '0 0 auto', minWidth: 0, padding: 12, overflowX: 'auto', overflowY: 'auto', height: TABLE_CONTAINER_HEIGHT, minHeight: TABLE_CONTAINER_HEIGHT }}>
            <table
              style={{
                width: 'max-content',
                minWidth: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'auto',
                fontSize: 12,
                fontFamily: 'inherit',
                color: '#1f2937',
                backgroundColor: '#fff',
              }}
            >
              {hasKa10027TableData && (
                <thead>
                  <tr>
                    {ka10027ColumnKeys.map((key) => (
                      <th key={key} style={{ position: 'sticky', top: 0, zIndex: 2, textAlign: 'center', padding: '4px 6px', borderBottom: '2px solid #e5e7eb', fontSize: 12, color: '#1f2937', backgroundColor: '#fff', whiteSpace: 'nowrap', boxShadow: '0 1px 0 0 #e5e7eb' }} title={key}>
                        <div style={{ fontWeight: 600, marginBottom: 1, fontSize: 11 }}>{key}</div>
                        <div style={{ fontSize: 11, color: '#374151' }}>{getKa10027ColumnLabelOnly(key)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {visibleMergedRows.length === 0 && (
                    <tr>
                    <td colSpan={ka10027ColumnKeys.length || 1} style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                      데이터 없음
                    </td>
                  </tr>
                )}
                {visibleMergedRows.map((mr, idx) => (
                  <tr key={`ka-${idx}-${mr.type}-${mr.ka27Row?.key ?? idx}`}>
                    {mr.type === 'ka10027' && mr.ka27Row ? (
                      (() => {
                        const row = mr.ka27Row;
                        return ka10027ColumnKeys.map((key) => {
                          const deltaStr = row[`${key}_delta`];
                          const deltaColor = deltaStr?.startsWith('-') ? '#2563eb' : '#dc2626';
                          return (
                            <td
                              key={key}
                              style={{
                                padding: '2px 6px',
                                borderBottom: '1px solid #f3f4f6',
                                textAlign: 'left',
                                fontSize: 12,
                                fontFamily: 'inherit',
                                backgroundColor: '#fff',
                                whiteSpace: 'nowrap',
                                minHeight: TABLE_ROW_HEIGHT,
                                boxSizing: 'border-box',
                              }}
                            >
                              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', color: '#1f2937', whiteSpace: 'nowrap' }} title={row[key] ?? '-'}>
                                <span>{row[key] ?? '-'}</span>
                                {deltaStr != null && <span style={{ fontSize: 10, color: deltaColor }}>{deltaStr}</span>}
                              </div>
                            </td>
                          );
                        });
                      })()
                    ) : (
                      ka10027ColumnKeys.map((key) => (
                        <td key={key} style={{ padding: '2px 6px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#fafafa', minHeight: TABLE_ROW_HEIGHT, boxSizing: 'border-box' }}>&nbsp;</td>
                      ))
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 0d 테이블 */}
        <div style={{ flex: '1 1 0%', minWidth: 0, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, fontSize: 14, flex: '0 0 auto', color: '#1f2937', backgroundColor: '#fff' }}>
            0d
          </div>
          <div ref={odTableScrollRef} onScroll={() => syncScroll(odTableScrollRef)} style={{ flex: '0 0 auto', minWidth: 0, padding: 12, overflowX: 'auto', overflowY: 'auto', height: TABLE_CONTAINER_HEIGHT, minHeight: TABLE_CONTAINER_HEIGHT }}>
            <table
              style={{
                width: 'max-content',
                minWidth: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'auto',
                fontSize: 12,
                fontFamily: 'inherit',
                color: '#1f2937',
                backgroundColor: '#fff',
              }}
            >
              {hasOdTableData && (
                <thead>
                  <tr>
                    <th style={{ position: 'sticky', top: 0, zIndex: 2, textAlign: 'center', padding: '4px 6px', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb', color: '#1f2937', backgroundColor: '#fff', whiteSpace: 'nowrap', boxShadow: '0 1px 0 0 #e5e7eb' }}>
                      collected_at
                    </th>
                    {odDataKeys.map((c) => (
                      <th key={c} style={{ position: 'sticky', top: 0, zIndex: 2, textAlign: 'center', padding: '4px 6px', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap', color: '#1f2937', backgroundColor: '#fff', boxShadow: '0 1px 0 0 #e5e7eb' }} title={c}>
                        <div style={{ fontWeight: 600, marginBottom: 1, fontSize: 11 }}>{c}</div>
                        <div style={{ fontSize: 11, color: '#374151' }}>{getColumnLabelOnly(c)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {visibleMergedRows.length === 0 && (
                  <tr>
                    <td colSpan={odColumnKeys.length} style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                      데이터 없음
                    </td>
                  </tr>
                )}
                {visibleMergedRows.map((mr, idx) => (
                  <tr key={`0d-${idx}-${mr.type}-${mr.odRow?.key ?? mr.ka27Row?.key ?? idx}`}>
                    {mr.type === '0d' && mr.odRow ? (
                      <>
                        <td
                          style={{
                            padding: '2px 6px',
                            borderBottom: '1px solid #f3f4f6',
                            borderRight: '1px solid #e5e7eb',
                            fontSize: 12,
                            fontFamily: 'inherit',
                            textAlign: 'left',
                            color: '#1f2937',
                            backgroundColor: '#fff',
                            minHeight: TABLE_ROW_HEIGHT,
                            boxSizing: 'border-box',
                          }}
                        >
                          {mr.odRow.collected_at}
                        </td>
                        {mr.odRow.cells.map((cell, ci) => (
                          <td
                            key={odDataKeys[ci]}
                            style={{
                              padding: '2px 6px',
                              borderBottom: '1px solid #f3f4f6',
                              textAlign: 'right',
                              whiteSpace: 'nowrap',
                              fontSize: 12,
                              fontFamily: 'inherit',
                              backgroundColor: '#fff',
                              minHeight: TABLE_ROW_HEIGHT,
                              boxSizing: 'border-box',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, alignItems: 'baseline', color: '#1f2937', whiteSpace: 'nowrap' }} title={cell.value}>
                              <span>{cell.value}</span>
                              <span style={{ fontSize: 10, color: cell.delta.startsWith('-') ? '#2563eb' : '#dc2626' }}>{cell.delta}</span>
                            </div>
                          </td>
                        ))}
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '2px 6px', borderBottom: '1px solid #f3f4f6', borderRight: '1px solid #e5e7eb', fontSize: 12, backgroundColor: '#fafafa', minHeight: TABLE_ROW_HEIGHT, boxSizing: 'border-box' }}>&nbsp;</td>
                        {odDataKeys.map((c) => (
                          <td key={c} style={{ padding: '2px 6px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#fafafa', minHeight: TABLE_ROW_HEIGHT, boxSizing: 'border-box' }}>&nbsp;</td>
                        ))}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 0c 테이블 */}
        <div style={{ flex: '1 1 0%', minWidth: 0, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, fontSize: 14, flex: '0 0 auto', color: '#1f2937', backgroundColor: '#fff' }}>
            0c
          </div>
          <div ref={ocTableScrollRef} onScroll={() => syncScroll(ocTableScrollRef)} style={{ flex: '0 0 auto', minWidth: 0, padding: 12, overflowX: 'auto', overflowY: 'auto', height: TABLE_CONTAINER_HEIGHT, minHeight: TABLE_CONTAINER_HEIGHT }}>
            <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', tableLayout: 'auto', fontSize: 12, fontFamily: 'inherit', color: '#1f2937', backgroundColor: '#fff' }}>
              {hasOcTableData && (
                <thead>
                  <tr>
                    {ocColumnKeys.map((key) => (
                      <th key={key} style={{ position: 'sticky', top: 0, zIndex: 2, textAlign: 'center', padding: '4px 6px', borderBottom: '2px solid #e5e7eb', fontSize: 12, color: '#1f2937', backgroundColor: '#fff', whiteSpace: 'nowrap', boxShadow: '0 1px 0 0 #e5e7eb' }} title={key}>
                        <div style={{ fontWeight: 600, marginBottom: 1, fontSize: 11 }}>{key}</div>
                        <div style={{ fontSize: 11, color: '#374151' }}>{getOcColumnLabelOnly(key)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {visibleMergedRows.length === 0 && (
                  <tr>
                    <td colSpan={ocColumnKeys.length || 1} style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>데이터 없음</td>
                  </tr>
                )}
                {visibleMergedRows.map((mr, idx) => (
                  <tr key={`oc-${idx}-${mr.type}-${mr.ocRow?.key ?? idx}`}>
                    {mr.type === '0c' && mr.ocRow ? ocColumnKeys.map((key) => {
                      const row = mr.ocRow!;
                      const deltaStr = row[`${key}_delta`];
                      const deltaColor = deltaStr?.startsWith('-') ? '#2563eb' : '#dc2626';
                      return (
                        <td key={key} style={{ padding: '2px 6px', borderBottom: '1px solid #f3f4f6', textAlign: 'left', fontSize: 12, fontFamily: 'inherit', backgroundColor: '#fff', whiteSpace: 'nowrap', minHeight: TABLE_ROW_HEIGHT, boxSizing: 'border-box' }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', color: '#1f2937', whiteSpace: 'nowrap' }} title={row[key] ?? '-'}>
                            <span>{row[key] ?? '-'}</span>
                            {deltaStr != null && <span style={{ fontSize: 10, color: deltaColor }}>{deltaStr}</span>}
                          </div>
                        </td>
                      );
                    }) : ocColumnKeys.map((key) => (
                      <td key={key} style={{ padding: '2px 6px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#fafafa', minHeight: TABLE_ROW_HEIGHT, boxSizing: 'border-box' }}>&nbsp;</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 1h 테이블 */}
        <div style={{ flex: '1 1 0%', minWidth: 0, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, fontSize: 14, flex: '0 0 auto', color: '#1f2937', backgroundColor: '#fff' }}>
            1h
          </div>
          <div ref={oh1TableScrollRef} onScroll={() => syncScroll(oh1TableScrollRef)} style={{ flex: '0 0 auto', minWidth: 0, padding: 12, overflowX: 'auto', overflowY: 'auto', height: TABLE_CONTAINER_HEIGHT, minHeight: TABLE_CONTAINER_HEIGHT }}>
            <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', tableLayout: 'auto', fontSize: 12, fontFamily: 'inherit', color: '#1f2937', backgroundColor: '#fff' }}>
              {hasOh1TableData && (
                <thead>
                  <tr>
                    {oh1ColumnKeys.map((key) => (
                      <th key={key} style={{ position: 'sticky', top: 0, zIndex: 2, textAlign: 'center', padding: '4px 6px', borderBottom: '2px solid #e5e7eb', fontSize: 12, color: '#1f2937', backgroundColor: '#fff', whiteSpace: 'nowrap', boxShadow: '0 1px 0 0 #e5e7eb' }} title={key}>
                        <div style={{ fontWeight: 600, marginBottom: 1, fontSize: 11 }}>{key}</div>
                        <div style={{ fontSize: 11, color: '#374151' }}>{getOh1ColumnLabelOnly(key)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {visibleMergedRows.length === 0 && (
                  <tr>
                    <td colSpan={oh1ColumnKeys.length || 1} style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>데이터 없음</td>
                  </tr>
                )}
                {visibleMergedRows.map((mr, idx) => (
                  <tr key={`oh1-${idx}-${mr.type}-${mr.oh1Row?.key ?? idx}`}>
                    {mr.type === '1h' && mr.oh1Row ? oh1ColumnKeys.map((key) => {
                      const row = mr.oh1Row!;
                      const deltaStr = row[`${key}_delta`];
                      const deltaColor = deltaStr?.startsWith('-') ? '#2563eb' : '#dc2626';
                      return (
                        <td key={key} style={{ padding: '2px 6px', borderBottom: '1px solid #f3f4f6', textAlign: 'left', fontSize: 12, fontFamily: 'inherit', backgroundColor: '#fff', whiteSpace: 'nowrap', minHeight: TABLE_ROW_HEIGHT, boxSizing: 'border-box' }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', color: '#1f2937', whiteSpace: 'nowrap' }} title={row[key] ?? '-'}>
                            <span>{row[key] ?? '-'}</span>
                            {deltaStr != null && <span style={{ fontSize: 10, color: deltaColor }}>{deltaStr}</span>}
                          </div>
                        </td>
                      );
                    }) : oh1ColumnKeys.map((key) => (
                      <td key={key} style={{ padding: '2px 6px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#fafafa', minHeight: TABLE_ROW_HEIGHT, boxSizing: 'border-box' }}>&nbsp;</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ka10095 테이블 */}
        <div style={{ flex: '1 1 0%', minWidth: 0, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, fontSize: 14, flex: '0 0 auto', color: '#1f2937', backgroundColor: '#fff' }}>
            ka10095
          </div>
          <div ref={ka10095TableScrollRef} onScroll={() => syncScroll(ka10095TableScrollRef)} style={{ flex: '0 0 auto', minWidth: 0, padding: 12, overflowX: 'auto', overflowY: 'auto', height: TABLE_CONTAINER_HEIGHT, minHeight: TABLE_CONTAINER_HEIGHT }}>
            <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', tableLayout: 'auto', fontSize: 12, fontFamily: 'inherit', color: '#1f2937', backgroundColor: '#fff' }}>
              {hasKa10095TableData && (
                <thead>
                  <tr>
                    {ka10095ColumnKeys.map((key) => (
                      <th key={key} style={{ position: 'sticky', top: 0, zIndex: 2, textAlign: 'center', padding: '4px 6px', borderBottom: '2px solid #e5e7eb', fontSize: 12, color: '#1f2937', backgroundColor: '#fff', whiteSpace: 'nowrap', boxShadow: '0 1px 0 0 #e5e7eb' }} title={key}>
                        <div style={{ fontWeight: 600, marginBottom: 1, fontSize: 11 }}>{key}</div>
                        <div style={{ fontSize: 11, color: '#374151' }}>{getKa10095ColumnLabelOnly(key)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {visibleMergedRows.length === 0 && (
                  <tr>
                    <td colSpan={ka10095ColumnKeys.length || 1} style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>데이터 없음</td>
                  </tr>
                )}
                {visibleMergedRows.map((mr, idx) => (
                  <tr key={`ka95-${idx}-${mr.type}-${mr.ka10095Row?.key ?? idx}`}>
                    {mr.type === 'ka10095' && mr.ka10095Row ? ka10095ColumnKeys.map((key) => {
                      const row = mr.ka10095Row!;
                      const deltaStr = row[`${key}_delta`];
                      const deltaColor = deltaStr?.startsWith('-') ? '#2563eb' : '#dc2626';
                      return (
                        <td key={key} style={{ padding: '2px 6px', borderBottom: '1px solid #f3f4f6', textAlign: 'left', fontSize: 12, fontFamily: 'inherit', backgroundColor: '#fff', whiteSpace: 'nowrap', minHeight: TABLE_ROW_HEIGHT, boxSizing: 'border-box' }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', color: '#1f2937', whiteSpace: 'nowrap' }} title={row[key] ?? '-'}>
                            <span>{row[key] ?? '-'}</span>
                            {deltaStr != null && <span style={{ fontSize: 10, color: deltaColor }}>{deltaStr}</span>}
                          </div>
                        </td>
                      );
                    }) : ka10095ColumnKeys.map((key) => (
                      <td key={key} style={{ padding: '2px 6px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#fafafa', minHeight: TABLE_ROW_HEIGHT, boxSizing: 'border-box' }}>&nbsp;</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Orderbook0dColumnCharts;
